import adminRequest from 'utils/adminRequest'
import studentRequest from 'utils/studentRequest'
import instRequest from 'utils/instRequest'
import corporateRequest from 'utils/corporateRequest'
import authRequest from 'utils/authRequest'
import qs from 'qs'

// Phase 2: in-memory TTL cache for the 4 cascading filter-dropdown actions
// (getAssessmentDomains/Degrees/Departments/Specializations). Same payload
// for the same param set within TTL — cuts 4 calls per Assessment page mount
// to 0 on subsequent mounts. 5-minute TTL keeps it small and self-healing.
const FILTER_CACHE_TTL_MS = 5 * 60 * 1000
const filterCache = new Map()
const filterInflight = new Map()
function filterCacheKey(name, params) {
  return name + '|' + JSON.stringify(params)
}
async function withFilterCache(name, params, loader) {
  const key = filterCacheKey(name, params)
  const now = Date.now()
  const hit = filterCache.get(key)
  if (hit && hit.expiresAt > now) return hit.value
  if (filterInflight.has(key)) return filterInflight.get(key)
  const promise = (async () => {
    try {
      const value = await loader()
      filterCache.set(key, { value, expiresAt: Date.now() + FILTER_CACHE_TTL_MS })
      return value
    } finally {
      filterInflight.delete(key)
    }
  })()
  filterInflight.set(key, promise)
  return promise
}

// Action Types
export const SET_LOADING = 'SET_LOADING'
export const SET_ERROR = 'SET_ERROR'
export const SET_ACTIVE_ASSESSMENT_COUNT = 'SET_ACTIVE_ASSESSMENT_COUNT'
export const SET_ASSESSMENT_COMPLETED_COUNT = 'SET_ASSESSMENT_COMPLETED_COUNT'
export const SET_ACTIVE_ASSESSMENTS = 'SET_ACTIVE_ASSESSMENTS'
export const SET_COMPLETED_ASSESSMENTS = 'SET_COMPLETED_ASSESSMENTS'
export const SET_ACTIVE_ASSESSMENTS_PAGINATION =
  'SET_ACTIVE_ASSESSMENTS_PAGINATION'
export const SET_COMPLETED_ASSESSMENTS_PAGINATION =
  'SET_COMPLETED_ASSESSMENTS_PAGINATION'
export const SET_ACTIVE_ASSESSMENTS_METRICS = 'SET_ACTIVE_ASSESSMENTS_METRICS'
export const SET_COMPLETED_ASSESSMENTS_METRICS =
  'SET_COMPLETED_ASSESSMENTS_METRICS'
export const SET_FILTERS = 'SET_FILTERS'
export const UPDATE_PAGE = 'UPDATE_PAGE'

export const SET_STUDENT_DATA_SENT = 'SET_STUDENT_DATA_SENT'
export const SET_STUDENT_DATA_PENDING = 'SET_STUDENT_DATA_PENDING'
export const SET_STUDENT_DATA_IN_PROGRESS = 'SET_STUDENT_DATA_IN_PROGRESS'
export const SET_STUDENT_DATA_DROPPED_OFF = 'SET_STUDENT_DATA_DROPPED_OFF'
export const SET_STUDENT_DATA_COMPLETED = 'SET_STUDENT_DATA_COMPLETED'
export const SET_STUDENT_DATA_LOADING = 'SET_STUDENT_DATA_LOADING'
export const SET_ASSESSMENT_FILTERS = 'SET_ASSESSMENT_FILTERS'
export const SET_CHART_FILTERS = 'SET_CHART_FILTERS'
export const CLEAR_CHART_FILTERS = 'CLEAR_CHART_FILTERS'
export const FETCH_ACTIVITY_MAP_REQUEST = 'FETCH_ACTIVITY_MAP_REQUEST'
export const FETCH_ACTIVITY_MAP_SUCCESS = 'FETCH_ACTIVITY_MAP_SUCCESS'
export const FETCH_ACTIVITY_MAP_FAILURE = 'FETCH_ACTIVITY_MAP_FAILURE'
export const SET_ACTIVITY_DATE_RANGE = 'SET_ACTIVITY_DATE_RANGE'

export const FETCH_PRACTICE_SCORES_REQUEST = 'FETCH_PRACTICE_SCORES_REQUEST'
export const FETCH_PRACTICE_SCORES_SUCCESS = 'FETCH_PRACTICE_SCORES_SUCCESS'
export const FETCH_PRACTICE_SCORES_FAILURE = 'FETCH_PRACTICE_SCORES_FAILURE'

// Add aptitude practice scores actions
export const FETCH_APTITUDE_PRACTICE_SCORES_REQUEST = 'FETCH_APTITUDE_PRACTICE_SCORES_REQUEST'
export const FETCH_APTITUDE_PRACTICE_SCORES_SUCCESS = 'FETCH_APTITUDE_PRACTICE_SCORES_SUCCESS'
export const FETCH_APTITUDE_PRACTICE_SCORES_FAILURE = 'FETCH_APTITUDE_PRACTICE_SCORES_FAILURE'

// Add actual assessment scores actions (is_practice=false)
export const FETCH_PRACTICE_SCORES2_REQUEST = 'FETCH_PRACTICE_SCORES2_REQUEST'
export const FETCH_PRACTICE_SCORES2_SUCCESS = 'FETCH_PRACTICE_SCORES2_SUCCESS'

export const FETCH_SPECIFIC_ASSESSMENT_STUDENT_LIST_REQUEST =
  'FETCH_SPECIFIC_ASSESSMENT_STUDENT_LIST_REQUEST'
export const FETCH_SPECIFIC_ASSESSMENT_STUDENT_LIST_SUCCESS =
  'FETCH_SPECIFIC_ASSESSMENT_STUDENT_LIST_SUCCESS'
export const FETCH_SPECIFIC_ASSESSMENT_STUDENT_LIST_FAILURE =
  'FETCH_SPECIFIC_ASSESSMENT_STUDENT_LIST_FAILURE'
export const FETCH_PRACTICE_SCORES2_FAILURE = 'FETCH_PRACTICE_SCORES2_FAILURE'

export const FETCH_APTITUDE_PRACTICE_SCORES2_REQUEST = 'FETCH_APTITUDE_PRACTICE_SCORES2_REQUEST'
export const FETCH_APTITUDE_PRACTICE_SCORES2_SUCCESS = 'FETCH_APTITUDE_PRACTICE_SCORES2_SUCCESS'
export const FETCH_APTITUDE_PRACTICE_SCORES2_FAILURE = 'FETCH_APTITUDE_PRACTICE_SCORES2_FAILURE'

// Add these new action types
export const FETCH_COMPLETED_PRACTICE_ASSESSMENTS_REQUEST = 'FETCH_COMPLETED_PRACTICE_ASSESSMENTS_REQUEST'
export const FETCH_COMPLETED_PRACTICE_ASSESSMENTS_SUCCESS = 'FETCH_COMPLETED_PRACTICE_ASSESSMENTS_SUCCESS'
export const FETCH_COMPLETED_PRACTICE_ASSESSMENTS_FAILURE = 'FETCH_COMPLETED_PRACTICE_ASSESSMENTS_FAILURE'

export const FETCH_LAST_ASSESSMENT_REQUEST = 'FETCH_LAST_ASSESSMENT_REQUEST'
export const FETCH_LAST_ASSESSMENT_SUCCESS = 'FETCH_LAST_ASSESSMENT_SUCCESS'
export const FETCH_LAST_ASSESSMENT_FAILURE = 'FETCH_LAST_ASSESSMENT_FAILURE'

export const FETCH_LAST_PRACTICE_REQUEST = 'FETCH_LAST_PRACTICE_REQUEST'
export const FETCH_LAST_PRACTICE_SUCCESS = 'FETCH_LAST_PRACTICE_SUCCESS'
export const FETCH_LAST_PRACTICE_FAILURE = 'FETCH_LAST_PRACTICE_FAILURE'

// Add these new action types with other action types
export const FETCH_CEFR_LEVEL_REQUEST = 'FETCH_CEFR_LEVEL_REQUEST'
export const FETCH_CEFR_LEVEL_SUCCESS = 'FETCH_CEFR_LEVEL_SUCCESS'
export const FETCH_CEFR_LEVEL_FAILURE = 'FETCH_CEFR_LEVEL_FAILURE'

// Add these action types at the top
export const FETCH_REPORTS_REQUEST = 'FETCH_REPORTS_REQUEST'
export const FETCH_REPORTS_SUCCESS = 'FETCH_REPORTS_SUCCESS'
export const FETCH_REPORTS_FAILURE = 'FETCH_REPORTS_FAILURE'

export const FETCH_ASSESSMENT_STATES_REQUEST = 'FETCH_ASSESSMENT_STATES_REQUEST'
export const FETCH_ASSESSMENT_STATES_SUCCESS = 'FETCH_ASSESSMENT_STATES_SUCCESS'
export const FETCH_ASSESSMENT_STATES_FAILURE = 'FETCH_ASSESSMENT_STATES_FAILURE'

export const FETCH_TPO_STUDENT_LIST_REQUEST = 'FETCH_TPO_STUDENT_LIST_REQUEST'
export const FETCH_TPO_STUDENT_LIST_SUCCESS = 'FETCH_TPO_STUDENT_LIST_SUCCESS'
export const FETCH_TPO_STUDENT_LIST_FAILURE = 'FETCH_TPO_STUDENT_LIST_FAILURE'

export const FETCH_MEDIA_KEYS_REQUEST = 'FETCH_MEDIA_KEYS_REQUEST'
export const FETCH_MEDIA_KEYS_SUCCESS = 'FETCH_MEDIA_KEYS_SUCCESS'
export const FETCH_MEDIA_KEYS_FAILURE = 'FETCH_MEDIA_KEYS_FAILURE'

export const FETCH_MEDIA_URLS_REQUEST = 'FETCH_MEDIA_URLS_REQUEST'
export const FETCH_MEDIA_URLS_SUCCESS = 'FETCH_MEDIA_URLS_SUCCESS'
export const FETCH_MEDIA_URLS_FAILURE = 'FETCH_MEDIA_URLS_FAILURE'

export const CHECK_REPORT_AVAILABILITY_REQUEST = 'CHECK_REPORT_AVAILABILITY_REQUEST'
export const CHECK_REPORT_AVAILABILITY_SUCCESS = 'CHECK_REPORT_AVAILABILITY_SUCCESS'
export const CHECK_REPORT_AVAILABILITY_FAILURE = 'CHECK_REPORT_AVAILABILITY_FAILURE'

export const RESET_MEDIA_STATE = 'RESET_MEDIA_STATE'

export const FETCH_ASSESSMENT_DETAILS_REQUEST = 'FETCH_ASSESSMENT_DETAILS_REQUEST'
export const FETCH_ASSESSMENT_DETAILS_SUCCESS = 'FETCH_ASSESSMENT_DETAILS_SUCCESS'
export const FETCH_ASSESSMENT_DETAILS_FAILURE = 'FETCH_ASSESSMENT_DETAILS_FAILURE'

// Chart API action types
export const FETCH_ASSESSMENT_CHARTS_REQUEST = 'FETCH_ASSESSMENT_CHARTS_REQUEST'
export const FETCH_ASSESSMENT_CHARTS_SUCCESS = 'FETCH_ASSESSMENT_CHARTS_SUCCESS'
export const FETCH_ASSESSMENT_CHARTS_FAILURE = 'FETCH_ASSESSMENT_CHARTS_FAILURE'
export const FETCH_CEFR_CHART_DATA_REQUEST = 'FETCH_CEFR_CHART_DATA_REQUEST'
export const FETCH_CEFR_CHART_DATA_SUCCESS = 'FETCH_CEFR_CHART_DATA_SUCCESS'
export const FETCH_CEFR_CHART_DATA_FAILURE = 'FETCH_CEFR_CHART_DATA_FAILURE'
export const FETCH_CONSISTENCY_DATA_REQUEST = 'FETCH_CONSISTENCY_DATA_REQUEST'
export const FETCH_CONSISTENCY_DATA_SUCCESS = 'FETCH_CONSISTENCY_DATA_SUCCESS'
export const FETCH_CONSISTENCY_DATA_FAILURE = 'FETCH_CONSISTENCY_DATA_FAILURE'
export const FETCH_APTITUDE_GROUPS_REQUEST = 'FETCH_APTITUDE_GROUPS_REQUEST'
export const FETCH_APTITUDE_GROUPS_SUCCESS = 'FETCH_APTITUDE_GROUPS_SUCCESS'
export const FETCH_APTITUDE_GROUPS_FAILURE = 'FETCH_APTITUDE_GROUPS_FAILURE'

// Add action to fetch aptitude specific metrics
export const FETCH_APTITUDE_METRICS_REQUEST = 'FETCH_APTITUDE_METRICS_REQUEST'
export const FETCH_APTITUDE_METRICS_SUCCESS = 'FETCH_APTITUDE_METRICS_SUCCESS'
export const FETCH_APTITUDE_METRICS_FAILURE = 'FETCH_APTITUDE_METRICS_FAILURE'

// Diagnosis data action types
export const FETCH_DIAGNOSIS_DATA_REQUEST = 'FETCH_DIAGNOSIS_DATA_REQUEST'
export const FETCH_DIAGNOSIS_DATA_SUCCESS = 'FETCH_DIAGNOSIS_DATA_SUCCESS'
export const FETCH_DIAGNOSIS_DATA_FAILURE = 'FETCH_DIAGNOSIS_DATA_FAILURE'

// Chart filter action types
// export const SET_CHART_FILTERS = 'SET_CHART_FILTERS'
// export const CLEAR_CHART_FILTERS = 'CLEAR_CHART_FILTERS'


export const resetMediaState = () => ({
  type: RESET_MEDIA_STATE,
})

// Action Creators
export const setLoading = status => ({
  type: SET_LOADING,
  status,
})

export const setError = error => ({
  type: SET_ERROR,
  error,
})

export const setActiveAssessmentCount = count => ({
  type: SET_ACTIVE_ASSESSMENT_COUNT,
  count,
})

export const setAssessmentCompletedCount = count => ({
  type: SET_ASSESSMENT_COMPLETED_COUNT,
  count,
})

export const setActiveAssessments = assessments => ({
  type: SET_ACTIVE_ASSESSMENTS,
  assessments,
})

export const setCompletedAssessments = assessments => ({
  type: SET_COMPLETED_ASSESSMENTS,
  assessments,
})

export const setActiveAssessmentsPagination = pagination => ({
  type: SET_ACTIVE_ASSESSMENTS_PAGINATION,
  pagination,
})

export const setCompletedAssessmentsPagination = pagination => ({
  type: SET_COMPLETED_ASSESSMENTS_PAGINATION,
  pagination,
})

export const setActiveAssessmentsMetrics = metrics => ({
  type: SET_ACTIVE_ASSESSMENTS_METRICS,
  metrics,
})

export const setCompletedAssessmentsMetrics = metrics => ({
  type: SET_COMPLETED_ASSESSMENTS_METRICS,
  metrics,
})

export const setFilters = filters => ({
  type: SET_FILTERS,
  filters,
})

export const updatePage = page => ({
  type: UPDATE_PAGE,
  page,
})

// for student data And assessment details
export const setStudentDataSent = students => ({
  type: SET_STUDENT_DATA_SENT,
  students,
})

export const setStudentDataPending = students => ({
  type: SET_STUDENT_DATA_PENDING,
  students,
})

export const setStudentDataInProgress = students => ({
  type: SET_STUDENT_DATA_IN_PROGRESS,
  students,
})

export const setStudentDataDroppedOff = students => ({
  type: SET_STUDENT_DATA_DROPPED_OFF,
  students,
})

export const setStudentDataCompleted = students => ({
  type: SET_STUDENT_DATA_COMPLETED,
  students,
})

export const setStudentDataLoading = status => ({
  type: SET_STUDENT_DATA_LOADING,
  status,
})

export const setAssessmentFilters = filters => ({
  type: SET_ASSESSMENT_FILTERS,
  filters,
})

// Chart-based filter actions
export const setChartFilters = filters => {
  console.log('🔧 [Redux Action] setChartFilters called with:', filters)
  return {
    type: SET_CHART_FILTERS,
    payload: filters,
  }
}

export const clearChartFilters = () => dispatch => {
  dispatch({ type: CLEAR_CHART_FILTERS })
}

// Add new actions for fetching filter options
export const getInstituteDomains = assessmentInstituteMapId => async dispatch => {
  try {
    const response = await instRequest.get(
      `/institutes/studentListInfo/domains`,
      { params: { assessmentInstituteMapId } }
    )
    const raw = Array.isArray(response?.data?.data) ? response.data.data : []
    // Normalize to legacy shape: [{ id, name }]
    return raw.map(item => ({ id: item.domainId, name: item.domainName }))
  } catch (error) {
    console.error('Error fetching domains (studentListInfo):', error)
    return []
  }
}

export const getInstituteDegrees = (instituteId, domainIds) => async dispatch => {
  try {
    const domainIdParam = domainIds.join(',')
    const response = await instRequest.get(
      `/institutes/${instituteId}/degree?domainId=${domainIdParam}&search=`
    )
    return response.data.data
  } catch (error) {
    console.error('Error fetching institute degrees:', error)
    return []
  }
}

export const getInstituteDepartments = (instituteId, degreeIds, domainIds) => async dispatch => {
  try {
    const payload = {
      degreeId: degreeIds.join(','),
      domainId: domainIds.join(','),
      jobCategory: '',
      search: ''
    }
    const response = await instRequest.post(
      `/institutes/${instituteId}/streams`,
      payload
    )
    return response.data.data
  } catch (error) {
    console.error('Error fetching institute departments (streams):', error)
    return []
  }
}

export const getInstituteSpecializations = (instituteId, degreeIds, domainIds, streamIds) => async dispatch => {
  try {
    const payload = {
      degreeId: degreeIds.join(','),
      domainId: domainIds.join(','),
      streamId: streamIds.join(','),
      jobCategory: '',
      search: ''
    }
    const response = await instRequest.post(
      `/institutes/${instituteId}/specialisations`,
      payload
    )
    return response.data.data
  } catch (error) {
    console.error('Error fetching institute specializations:', error)
    return []
  }
}

export const getYearOfPassing = instituteId => async dispatch => {
  try {
    const response = await studentRequest.get(
      `/students/institutes/${instituteId}/yearOfPassing`
    )
    return response.data.data
  } catch (error) {
    console.error('Error fetching year of passing:', error)
    return []
  }
}

export const getAssessmentDomains = (params = {}) => async dispatch =>
  withFilterCache('domains', params, async () => {
    try {
      const queryParams = new URLSearchParams()
      // Support both assessmentId and scheduleId for diagnosis
      const id = params.assessmentId || params.assessmentInstituteMapId
      if (id) queryParams.append('assessmentId', id)
      if (params.scheduleId) queryParams.append('scheduleId', params.scheduleId)
      if (params.passingYear) queryParams.append('passingYear', params.passingYear)
      if (params.instituteId) queryParams.append('instituteId', params.instituteId)

      const response = await studentRequest.get(
        `/assessment/getDomain?${queryParams.toString()}`
      )

      if (response.data?.message?.toLowerCase() === 'success') {
        // Return domains as options format: { label, value }
        const domains = response.data.data?.domains || []
        return domains.map(d => ({ label: d, value: d, name: d, id: d }))
      }
      return []
    } catch (error) {
      console.error('Error fetching assessment domains:', error)
      return []
    }
  })

export const getAssessmentDegrees = (params = {}) => async dispatch =>
  withFilterCache('degrees', params, async () => {
    try {
      const queryParams = new URLSearchParams()
      const id = params.assessmentId || params.assessmentInstituteMapId
      if (id) queryParams.append('assessmentId', id)
      if (params.scheduleId) queryParams.append('scheduleId', params.scheduleId)
      if (params.passingYear) queryParams.append('passingYear', params.passingYear)
      if (params.instituteId) queryParams.append('instituteId', params.instituteId)
      if (params.domains && params.domains.length > 0) {
        queryParams.append('domains', params.domains.join(','))
      }

      const response = await studentRequest.get(
        `/assessment/getDegree?${queryParams.toString()}`
      )

      if (response.data?.message?.toLowerCase() === 'success') {
        const degrees = response.data.data?.degrees || []
        return degrees.map(d => ({ label: d, value: d, name: d, id: d }))
      }
      return []
    } catch (error) {
      console.error('Error fetching assessment degrees:', error)
      return []
    }
  })

export const getAssessmentDepartments = (params = {}) => async dispatch =>
  withFilterCache('departments', params, async () => {
    try {
      const queryParams = new URLSearchParams()
      const id = params.assessmentId || params.assessmentInstituteMapId
      if (id) queryParams.append('assessmentId', id)
      if (params.scheduleId) queryParams.append('scheduleId', params.scheduleId)
      if (params.passingYear) queryParams.append('passingYear', params.passingYear)
      if (params.instituteId) queryParams.append('instituteId', params.instituteId)
      if (params.degrees && params.degrees.length > 0) {
        queryParams.append('degrees', params.degrees.join(','))
      }

      const response = await studentRequest.get(
        `/assessment/getDepartment?${queryParams.toString()}`
      )

      if (response.data?.message?.toLowerCase() === 'success') {
        const departments = response.data.data?.departments || []
        return departments.map(d => ({ label: d, value: d, name: d, id: d }))
      }
      return []
    } catch (error) {
      console.error('Error fetching assessment departments:', error)
      return []
    }
  })

export const getAssessmentSpecializations = (params = {}) => async dispatch =>
  withFilterCache('specializations', params, async () => {
    try {
      const queryParams = new URLSearchParams()
      const id = params.assessmentId || params.assessmentInstituteMapId
      if (id) queryParams.append('assessmentId', id)
      if (params.scheduleId) queryParams.append('scheduleId', params.scheduleId)
      if (params.passingYear) queryParams.append('passingYear', params.passingYear)
      if (params.instituteId) queryParams.append('instituteId', params.instituteId)
      if (params.departments && params.departments.length > 0) {
        queryParams.append('departments', params.departments.join(','))
      }

      const response = await studentRequest.get(
        `/assessment/getSpecialization?${queryParams.toString()}`
      )

      if (response.data?.message?.toLowerCase() === 'success') {
        const specializations = response.data.data?.specializations || []
        return specializations.map(s => ({ label: s, value: s, name: s, id: s }))
      }
      return []
    } catch (error) {
      console.error('Error fetching assessment specializations:', error)
      return []
    }
  })


// Thunk action to fetch detailed assessment data with filters
export const fetchAssessmentDetails = params => async dispatch => {
  dispatch({ type: FETCH_ASSESSMENT_DETAILS_REQUEST })
  try {
    // Frontend mock mode: bypass backend and dispatch a stable payload for UI validation
    const useMock = params?.useMockData === true ||
      (typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem('ASSESSMENT_MOCK') === 'true')

    if (useMock) {
      const mockData = {
        studentData: {
          sent: [
            {
              id: 'karanpg2306+testkaran4342@gmail.com',
              assessmentAssignedId: '0e92bc19-75a9-4a72-b0a6-ce0f23108bc5',
              studentName: '',
              studentNumber: 'fa20aaa2-62d8-453d-a024-6c7d9fd37159',
              student_img_url:
                'https://ui-avatars.com/api/?name=+&background=random&color=fff',
              phone: 'undefined undefined',
              email: 'karanpg2306+testkaran4342@gmail.com',
              degree: '-',
              department: '-',
              specialization: '-',
              skipCount: '1/1',
              sentDate: '22 Jul 2025',
              startDate: '-',
              endDate: '09 Aug 2025',
              droppedOffDateTime: '09 Aug 2025, 11:59 PM',
              scoresCalculated: false,
              assignedcefr: null,
            },
            {
              id: 'ravikotadiya07+6969@gmail.com',
              assessmentAssignedId: '57331a15-4d9c-4b30-acee-4f2dd8f32b35',
              studentName: 'r k',
              studentNumber: '3451bb65-6453-4dc0-bf84-3e869f9f4188',
              student_img_url:
                'https://ui-avatars.com/api/?name=r+k&background=random&color=fff',
              phone: '+91 ',
              email: 'ravikotadiya07+6969@gmail.com',
              degree: '-',
              department: '-',
              specialization: '-',
              skipCount: '1/1',
              sentDate: '22 Jul 2025',
              startDate: '-',
              endDate: '09 Aug 2025',
              droppedOffDateTime: '09 Aug 2025, 11:59 PM',
              scoresCalculated: false,
              assignedcefr: null,
              aptitudeLevel: 'Beginner',
            },
          ],
          pending: [
            {
              id: 'karanpg2306+testkaran4342@gmail.com',
              assessmentAssignedId: '0e92bc19-75a9-4a72-b0a6-ce0f23108bc5',
              studentName: '',
              studentNumber: 'fa20aaa2-62d8-453d-a024-6c7d9fd37159',
              student_img_url:
                'https://ui-avatars.com/api/?name=+&background=random&color=fff',
              phone: 'undefined undefined',
              email: 'karanpg2306+testkaran4342@gmail.com',
              degree: '-',
              department: '-',
              specialization: '-',
              skipCount: '1/1',
              sentDate: '22 Jul 2025',
              startDate: '-',
              endDate: '09 Aug 2025',
              droppedOffDateTime: '09 Aug 2025, 11:59 PM',
              scoresCalculated: false,
              assignedcefr: null,
            },
            {
              id: 'ravikotadiya07+6969@gmail.com',
              assessmentAssignedId: '57331a15-4d9c-4b30-acee-4f2dd8f32b35',
              studentName: 'r k',
              studentNumber: '3451bb65-6453-4dc0-bf84-3e869f9f4188',
              student_img_url:
                'https://ui-avatars.com/api/?name=r+k&background=random&color=fff',
              phone: '+91 ',
              email: 'ravikotadiya07+6969@gmail.com',
              degree: '-',
              department: '-',
              specialization: '-',
              skipCount: '1/1',
              sentDate: '22 Jul 2025',
              startDate: '-',
              endDate: '09 Aug 2025',
              droppedOffDateTime: '09 Aug 2025, 11:59 PM',
              scoresCalculated: false,
              assignedcefr: null,
              aptitudeLevel: 'Beginner',
            },
          ],
          inProgress: [],
          droppedOff: [],
          completed: [
            {
              id: 'rajendran.k+0312@pluginlive.com',
              assessmentAssignedId: '3abc1410-f26b-4724-b4ae-d720d38e2328',
              studentName: 'Raj k',
              studentNumber: 'f1c1e9b9-f6b9-425a-a162-e45ff018bfaa',
              student_img_url:
                'https://d3ukw023z30y0v.cloudfront.net/1741330236323-raj_profile.jpg',
              phone: '91 6380306084',
              email: 'rajendran.k+0312@pluginlive.com',
              degree: '-',
              department: '-',
              specialization: '-',
              skipCount: '0/1',
              sentDate: '22 Jul 2025',
              startDate: '24 Jul 2025, 3:23 PM',
              endDate: '09 Aug 2025',
              startedDateTime: '24 Jul 2025, 3:23 PM',
              droppedOffDateTime: '09 Aug 2025, 11:59 PM',
              scoresCalculated: true,
              assignedcefr: null,
              resultingCefr: null,
              aptitudeLevel: 'Beginner',
              aptitudeScores: {
                overallPercentage: 12.92,
                criticalReasoningPercentage: -8.33,
                quantitativePercentage: 16.67,
                logicalReasoningPercentage: 23.81,
              },
              flagged: null,
            },
          ],
        },
        assessmentInfo: {
          name: 'Assessment',
          startDate: '22 Jul 2025',
          endDate: '09 Aug 2025',
          totalStudents: 4,
          completedCount: 1,
          pendingCount: 2,
          droppedOffCount: 0,
          inProgressCount: 0,
          allowProctoring: true,
          assessmentType: 'Aptitude',
          isBehavioralAssessment: false,
          isAptitudeAssessment: true,
          cefrLevel: null,
        },
        pagination: {
          currentPage: 1,
          totalPages: 1,
          totalCount: 4,
          hasNextPage: false,
          hasPrevPage: false,
          nextPage: null,
          prevPage: null,
        },
      }

      dispatch({ type: FETCH_ASSESSMENT_DETAILS_SUCCESS, payload: mockData })
      return mockData
    }

    const apiParams = {
      assessmentInstituteMapID: params.assessmentId,
      entityType: params.entityType,
      pageNo: params.currentPage - 1,
      pageLimit: params.pageSize,
    }

    if (params.filters && Object.keys(params.filters).length > 0) {
      apiParams.filters = JSON.stringify(params.filters)
    }
    if (params.searchTerm) {
      apiParams.searchQuery = params.searchTerm
    }

    const response = await adminRequest.get('/assessment/getAssessmentDetails', {
      params: apiParams,
    })

    if (response.data && response.data.data) {
      // Debug: Log API params and a concise snapshot of studentData to validate field names
      try {
        console.log('🛰️ [fetchAssessmentDetails] API params ->', apiParams)
        const sd = response.data.data?.studentData || {}
        const pickSample = arr => (Array.isArray(arr) && arr.length > 0 ? arr[0] : null)
        const sentSample = pickSample(sd.sent)
        const pendingSample = pickSample(sd.pending)
        const inProgSample = pickSample(sd.inProgress)
        const droppedSample = pickSample(sd.droppedOff)
        const completedSample = pickSample(sd.completed)
        const simplify = s =>
          s
            ? {
              id: s.id || s.studentId || s.assessmentAssignedId,
              name: s.studentName || s.name,
              assignedcefr: s.assignedcefr,
              cefrLevel: s.cefrLevel || s.cefr_level,
              aptitudeLevel: s.aptitudeLevel || s.aptitude_level,
              consistency: s.consistencyLevel || s.consistency_level || s.consistency,
              keys: Object.keys(s || {}),
            }
            : null
        console.log('🧩 [fetchAssessmentDetails] studentData snapshot ->', {
          sent: simplify(sentSample),
          pending: simplify(pendingSample),
          inProgress: simplify(inProgSample),
          droppedOff: simplify(droppedSample),
          completed: simplify(completedSample),
        })

        // Extra: stringified, human-readable summary of detected level fields
        const summarize = (label, s) => {
          if (!s) return `${label}: (no sample)`
          const cefrPrimary = s.assignedcefr?.primary || s.cefrLevel || s.cefr_level || null
          const cefrSecondary = s.assignedcefr?.secondary || null
          const aptitude = s.aptitudeLevel || s.aptitude_level || null
          const rawKeys = Object.keys(s)
          return `${label}: id=${s.id || s.studentId || s.assessmentAssignedId || '-'} name=${s.studentName || s.name || '-'} | CEFR(primary=${cefrPrimary}, secondary=${cefrSecondary}) | Aptitude=${aptitude} | Keys=${rawKeys.join(',')}`
        }
        console.log(
          '🧾 [fetchAssessmentDetails] Detected fields ->\n' +
          [
            summarize('sent', sentSample),
            summarize('pending', pendingSample),
            summarize('inProgress', inProgSample),
            summarize('droppedOff', droppedSample),
            summarize('completed', completedSample),
          ].join('\n')
        )
      } catch (e) {
        console.warn('⚠️ [fetchAssessmentDetails] debug log failed:', e?.message)
      }
      dispatch({
        type: FETCH_ASSESSMENT_DETAILS_SUCCESS,
        payload: response.data.data,
      })
    } else {
      throw new Error(
        response.data.message || 'Failed to fetch assessment details'
      )
    }
  } catch (error) {
    dispatch({
      type: FETCH_ASSESSMENT_DETAILS_FAILURE,
      payload: error.message,
    })
  }
}

export const fetchActivityMapRequest = () => ({
  type: FETCH_ACTIVITY_MAP_REQUEST,
})

export const fetchActivityMapSuccess = data => ({
  type: FETCH_ACTIVITY_MAP_SUCCESS,
  payload: data,
})

export const fetchActivityMapFailure = error => ({
  type: FETCH_ACTIVITY_MAP_FAILURE,
  payload: error,
})

export const setActivityDateRange = (startDate, endDate) => ({
  type: SET_ACTIVITY_DATE_RANGE,
  payload: { startDate, endDate },
})

// Add these action creators
export const fetchReportsRequest = () => ({
  type: FETCH_REPORTS_REQUEST,
})

export const fetchReportsSuccess = (reports) => ({
  type: FETCH_REPORTS_SUCCESS,
  payload: reports,
})

export const fetchReportsFailure = (error) => ({
  type: FETCH_REPORTS_FAILURE,
  payload: error,
})

// Thunk Actions
export const fetchActiveAssessmentCount =
  (params = {}) =>
    async dispatch => {
      dispatch(setLoading(true))
      try {
        const apiParams = {
          entityType: params.entityType || 'college',
          type_name: params.type_name || '',
          searchBy: params.searchBy || '',
          ...(params.instituteId ? { instituteId: params.instituteId } : {}),
          ...(params.passingYear ? { passingYear: params.passingYear } : {}),
        }

        const response = await adminRequest.get(
          '/assessment/getActiveAssessmentCount',
          {
            params: apiParams,
          }
        )
        dispatch(setActiveAssessmentCount(response.data.data))
        return response.data
      } catch (error) {
        dispatch(setError(error.message))
        throw error
      } finally {
        dispatch(setLoading(false))
      }
    }

export const fetchAssessmentCompletedCount =
  (params = {}) =>
    async dispatch => {
      dispatch(setLoading(true))
      try {
        const apiParams = {
          entityType: params.entityType || 'college',
          type_name: params.type_name || '',
          searchBy: params.searchBy || '',
          ...(params.instituteId ? { instituteId: params.instituteId } : {}),
          ...(params.passingYear ? { passingYear: params.passingYear } : {}),
        }

        const response = await adminRequest.get(
          '/assessment/getAssessmentCompletedCount',
          {
            params: apiParams,
          }
        )
        dispatch(setAssessmentCompletedCount(response.data.data))
        return response.data
      } catch (error) {
        dispatch(setError(error.message))
        throw error
      } finally {
        dispatch(setLoading(false))
      }
    }

// Request counter to prevent stale responses from overwriting newer data
let _activeAssessmentsRequestId = 0

export const fetchActiveAssessments =
  (params = {}) =>
    async (dispatch, getState) => {
      // Guard: skip if no passingYear provided (prevents fetching all years)
      if (!params.passingYear) {
        console.warn('fetchActiveAssessments: skipping - no passingYear provided')
        return []
      }

      const requestId = ++_activeAssessmentsRequestId
      dispatch(setLoading(true))
      try {
        const state = getState()

        const currentFilters = state.SubscribedInstitutes?.filters || {}

        const queryParams = {
          pageNo: params.pageNo ?? currentFilters.pageNo ?? 0,
          pageLimit: params.pageLimit ?? currentFilters.pageLimit ?? 10,
          searchBy: params.searchBy ?? currentFilters.searchBy ?? '',
          order: params.order ?? currentFilters.order ?? 'createdAt',
          sort: params.sort ?? currentFilters.sort ?? 'desc',
          type_name: params.type_name ?? currentFilters.type_name ?? '',
          isTrial: params.isTrial ?? currentFilters.isTrial ?? false,
          isSubscribed:
            params.isSubscribed ?? currentFilters.isSubscribed ?? false,
          states: params.states ?? currentFilters.states ?? [],
          cities: params.cities ?? currentFilters.cities ?? [],
          entityType: params.entityType ?? currentFilters.entityType ?? 'college',
          instituteId: params.instituteId ?? currentFilters.instituteId ?? null,
          passingYear: params.passingYear ?? currentFilters.passingYear ?? null,
        }

        // Update filters in Redux store
        if (params.updateFilters) {
          dispatch(setFilters({ ...queryParams }))
        }

        // Update page separately if only page changed
        if (params.pageOnly) {
          dispatch(updatePage(params.pageNo))
        }

        // Create serialized params for API request
        const apiParams = {
          ...queryParams,
          states: queryParams.states?.length
            ? queryParams.states.join(',')
            : undefined,
          cities: queryParams.cities?.length
            ? queryParams.cities.join(',')
            : undefined,
        }

        const response = await instRequest.get(
          '/institutes/studentListInfo/getschedulesInfo',
          {
            params: apiParams,
            paramsSerializer: params => {
              return qs.stringify(params, { arrayFormat: 'comma' })
            },
          }
        )

        // Discard stale response if a newer request was made while this one was in flight
        if (requestId !== _activeAssessmentsRequestId) {
          console.log('fetchActiveAssessments: discarding stale response (requestId:', requestId, 'current:', _activeAssessmentsRequestId, ')')
          return []
        }

        // Process response
        const responseData = response.data.data

        dispatch(setActiveAssessments(responseData.data))
        dispatch(
          setActiveAssessmentsPagination({
            page: parseInt(responseData.pagination.currentPage) || 1,
            limit: parseInt(responseData.pagination.pageSize) || 10,
            totalItems: responseData.pagination.totalCount || 0,
            currentCount: responseData.pagination.currentCount || 0,
            totalPages: parseInt(responseData.pagination.totalPages) || 1,
            stateCityPairs: responseData.stateCityPairs || [],
          })
        )

        dispatch(
          setActiveAssessmentsMetrics({
            total: responseData.pagination.totalCount || 0,
            completed: responseData.data.reduce(
              (acc, curr) => acc + (curr.assessmentCompleted || 0),
              0
            ),
            pending: responseData.data.reduce(
              (acc, curr) =>
                acc +
                ((curr.assessmentSent || 0) - (curr.assessmentCompleted || 0)),
              0
            ),
            trialCount: responseData.trialCount || 0,
            subscribedCount: responseData.subscribedCount || 0,
          })
        )

        return responseData
      } catch (error) {
        console.error('Error fetching active assessments:', error)
        dispatch(setError(error.message || 'Failed to fetch active assessments'))
        return []
      } finally {
        dispatch(setLoading(false))
      }
    }

export const fetchCompletedAssessments =
  (params = {}) =>
    async (dispatch, getState) => {
      dispatch(setLoading(true))
      try {
        const state = getState()
        const currentFilters = state.SubscribedInstitutes.filters

        const queryParams = {
          pageNo: params.pageNo ?? currentFilters.pageNo ?? 0,
          pageLimit: params.pageLimit ?? currentFilters.pageLimit ?? 10,
          searchBy: params.searchBy ?? currentFilters.searchBy ?? '',
          order: params.order ?? currentFilters.order ?? 'endDate',
          sort: params.sort ?? currentFilters.sort ?? 'asc',
          type_name: params.type_name ?? currentFilters.type_name ?? '',
          isTrial: params.isTrial ?? currentFilters.isTrial ?? false,
          isSubscribed:
            params.isSubscribed ?? currentFilters.isSubscribed ?? false,
          states: params.states ?? currentFilters.states ?? [],
          cities: params.cities ?? currentFilters.cities ?? [],
          entityType: params.entityType ?? currentFilters.entityType ?? 'college',
          instituteId: params.instituteId ?? currentFilters.instituteId ?? null,
          passingYear: params.passingYear ?? currentFilters.passingYear ?? null,
        }

        // Update filters in Redux store
        if (params.updateFilters) {
          dispatch(setFilters({ ...queryParams }))
        }

        // Update page separately if only page changed
        if (params.pageOnly) {
          dispatch(updatePage(params.pageNo))
        }

        // Create serialized params for API request
        const apiParams = {
          ...queryParams,
          states: queryParams.states?.length
            ? queryParams.states.join(',')
            : undefined,
          cities: queryParams.cities?.length
            ? queryParams.cities.join(',')
            : undefined,
        }

        const response = await adminRequest.get(
          '/assessment/getCompletedAssessments',
          {
            params: apiParams,
            paramsSerializer: params => {
              return qs.stringify(params, { arrayFormat: 'comma' })
            },
          }
        )

        // Process response
        const responseData = response.data.data

        console.log('Completed assessments response:', responseData)
        console.log('Completed assessments data:', responseData.data.infoCards)
        console.log('Completed assessments data1:', responseData.data.totalCandidates)
        console.log('Completed assessments data2:', responseData.data.totalAssessmentsSent)
        console.log('Completed assessments data3:', responseData.data.totalAssessmentsTaken)

        dispatch(setCompletedAssessments(responseData.data))
        dispatch(
          setCompletedAssessmentsPagination({
            page: parseInt(responseData.page) || 0,
            limit: parseInt(responseData.limit) || 10,
            totalItems: responseData.count || 0,
            currentCount: responseData.currentCount || 0,
            totalPages: parseInt(responseData.totalPages) || 1,
            stateCityPairs: responseData.stateCityPairs || [],
          })
        )

        dispatch(
          setCompletedAssessmentsMetrics({
            total: responseData.count || 0,
            completedOnTime: Math.floor(responseData.data.length * 0.7),
            completedLate:
              responseData.data.length -
              Math.floor(responseData.data.length * 0.7),
            trialCount: responseData.trialCount || 0,
            subscribedCount: responseData.subscribedCount || 0,
          })
        )

        return responseData
      } catch (error) {
        console.error('Error fetching completed assessments:', error)
        dispatch(
          setError(error.message || 'Failed to fetch completed assessments')
        )
        return []
      } finally {
        dispatch(setLoading(false))
      }
    }

// Thunk actions for student data and assessment details
export const fetchStudentsByStatus =
  (assessmentId, status, entityType = 'college') =>
    async dispatch => {
      dispatch(setStudentDataLoading(true))
      try {
        const response = await adminRequest.get(
          '/assessment/getAssessmentDetails',
          {
            params: {
              assessmentInstituteMapID: assessmentId,
              entityType,
            },
          }
        )

        const studentData = response.data.data.studentData || {}
        const statusMap = {
          sent: 'sent',
          pending: 'pending',
          inProgress: 'inProgress',
          droppedOff: 'droppedOff',
          completed: 'completed',
        }

        const apiStatus = statusMap[status] || status
        const data = studentData[apiStatus] || []

        switch (status) {
          case 'sent':
            dispatch(setStudentDataSent(data))
            break
          case 'pending':
            dispatch(setStudentDataPending(data))
            break
          case 'inprogress':
            dispatch(setStudentDataInProgress(data))
            break
          case 'droppedoff':
            dispatch(setStudentDataDroppedOff(data))
            break
          case 'completed':
            dispatch(setStudentDataCompleted(data))
            break
        }

        return data
      } catch (error) {
        console.error('Error fetching student data:', error)
        return []
      } finally {
        dispatch(setStudentDataLoading(false))
      }
    }

export const fetchAllStudentStatusCounts =
  (assessmentId, entityType = 'college') =>
    async dispatch => {
      dispatch(setStudentDataLoading(true))
      try {
        const response = await adminRequest.get(
          '/assessment/getAssessmentDetails',
          {
            params: {
              assessmentInstituteMapID: assessmentId,
              entityType,
            },
          }
        )

        const studentData = response.data.data.studentData || {}

        dispatch(setStudentDataSent(studentData.sent || []))
        dispatch(setStudentDataPending(studentData.pending || []))
        dispatch(setStudentDataInProgress(studentData.inProgress || []))
        dispatch(setStudentDataDroppedOff(studentData.droppedOff || []))
        dispatch(setStudentDataCompleted(studentData.completed || []))

        return studentData
      } catch (error) {
        console.error('Error fetching student status counts:', error)
        return {}
      } finally {
        dispatch(setStudentDataLoading(false))
      }
    }

export const exportStudentData =
  (assessmentId, entityType = 'college', status = 'completed', options = {}) =>
    async dispatch => {
      dispatch(setStudentDataLoading(true))
      try {
        // Reflect the on-screen CandidateList filters in the export.
        const serverFilters = {}
        if (options.degree?.length) serverFilters.degree = options.degree
        if (options.department?.length) serverFilters.department = options.department
        if (options.specialization?.length) serverFilters.specialization = options.specialization
        if (options.status?.length) serverFilters.status = options.status

        const response = await adminRequest.get('/assessment/exportStudentData', {
          params: {
            assessmentInstituteMapID: assessmentId,
            entityType,
            status,
            ...(options.search ? { searchQuery: options.search } : {}),
            ...(Object.keys(serverFilters).length
              ? { filters: JSON.stringify(serverFilters) }
              : {}),
          },
          responseType: 'blob',
        })

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url

        // Get filename from headers if available
        const contentDisposition = response.headers['content-disposition']
        let filename = 'assessment-data.xlsx'

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/)
          if (filenameMatch && filenameMatch.length === 2) {
            filename = filenameMatch[1]
          }
        } else {
          filename = `Results-${new Date().toISOString().split('T')[0]}.xlsx`
        }

        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()

        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        return true
      } catch (error) {
        console.error('Error exporting student data:', error)
        return false
      } finally {
        dispatch(setStudentDataLoading(false))
      }
    }

export const sendRemindersToStudents =
  (assessmentId, entityType = 'college') =>
    async dispatch => {
      try {
        const response = await adminRequest.post('/assessment/sendReminders', {
          assessmentInstituteMapID: assessmentId,
          entityType,
        })

        //console.log('Reminders sent successfully', response.data)
        return true
      } catch (error) {
        console.error('Error sending reminders:', error)
        return false
      }
    }

export const resendInvitesToStudents =
  (assessmentId, entityType = 'college') =>
    async dispatch => {
      try {
        const response = await adminRequest.post('/assessment/resendInvites', {
          assessmentInstituteMapID: assessmentId,
          entityType,
        })

        //console.log('Invites resent successfully', response.data)
        return true
      } catch (error) {
        console.error('Error resending invites:', error)
        return false
      }
    }

export const fetchProctoringDetails =
  assessmentAssignedId => async dispatch => {
    dispatch(setLoading(true))
    try {
      if (!assessmentAssignedId) {
        throw new Error('Assessment assigned ID is required')
      }

      const response = await adminRequest.get(
        '/assessment/getProctoringDetails',
        {
          params: { assessmentAssignedId },
        }
      )
      return response.data.data
    } catch (error) {
      dispatch(setError(error.message))
      throw error
    } finally {
      dispatch(setLoading(false))
    }
  }

// FIXED: Make sure fetchActivityMap action is implemented correctly
export const fetchActivityMap = (
  primaryEmail,
  isPractice = true,
  startDate,
  endDate
) => {
  return async dispatch => {
    console.log('🔄 Fetching activity map:', { primaryEmail, isPractice, startDate, endDate })

    dispatch(fetchActivityMapRequest())

    try {
      const params = new URLSearchParams({
        primaryEmail: primaryEmail,
        is_practice: isPractice.toString(),
        startDate: startDate,
        endDate: endDate,
      })

      const response = await studentRequest.get(
        `students/getActivityMap?${params}`
      )

      console.log('📋 Activity map response:', response.data)

      if (response.data && response.data.message === 'success') {
        console.log('✅ Activity map success, data length:', response.data.data.length)
        dispatch(fetchActivityMapSuccess(response.data.data))
        return response.data.data
      } else {
        dispatch(fetchActivityMapFailure('Failed to fetch activity data'))
      }
    } catch (error) {
      console.error('❌ Activity map error:', error)
      dispatch(
        fetchActivityMapFailure(
          error.message || 'An error occurred while fetching activity data'
        )
      )
    }
  }
}


export const fetchPracticeScores = (email) => async (dispatch) => {
  try {
    console.log('🎯 fetchPracticeScores FUNCTION CALLED!')
    console.log('🎯 Email parameter received:', email)
    console.log('🎯 Using test email: rajendran.k+0312@pluginlive.com')

    // Use the test email for now
    const testEmail = 'rajendran.k+0312@pluginlive.com'

    dispatch({ type: FETCH_PRACTICE_SCORES_REQUEST });

    console.log('🔍 Using endpoint: students/practice-scores/' + encodeURIComponent(email))
    const response = await studentRequest.get(`students/practice-scores/${encodeURIComponent(email)}`);
    const result = response.data;

    console.log('🔍 Practice API Response:', result)
    console.log('🔍 Practice Response data:', result.data)
    console.log('🔍 Practice Response message:', result.message)

    if (result.message === 'success') {
      console.log('✅ Success - dispatching practice data:', result.data)

      // Handle empty object response by converting to empty array (same as Assessment-React)
      let practiceData = result.data
      if (!practiceData || (typeof practiceData === 'object' && !Array.isArray(practiceData) && Object.keys(practiceData).length === 0)) {
        console.log('🔍 Practice data is empty object, converting to empty array')
        practiceData = []
      } else if (!Array.isArray(practiceData)) {
        console.warn('Practice scores data is not an array, converting to array')
        practiceData = []
      }

      // Extract dayZero data if available (Communication uses "dataZero" key)
      const dayZero = result.dataZero || result.dayZero || null
      console.log('🎯 Communication dayZero data extracted:', dayZero)

      const payload = {
        data: practiceData,
        dayZero: dayZero
      }

      dispatch({
        type: FETCH_PRACTICE_SCORES_SUCCESS,
        payload: payload
      });
      return payload;
    } else {
      console.error('❌ API returned non-success message:', result.error || 'Unknown error');
      throw new Error(result.error || 'Failed to fetch practice scores');
    }
  } catch (error) {
    console.error('❌ Practice scores exception:', error);
    console.error('❌ Error status:', error.response?.status);
    console.error('❌ Error data:', error.response?.data);
    console.error('❌ Error config URL:', error.config?.url);

    // If we get 404, try alternative endpoints
    if (error.response?.status === 404) {
      console.log('🔄 404 error, trying alternative practice endpoints...')
      return await tryAlternativePracticeEndpoints(dispatch, testEmail);
    }

    dispatch({
      type: FETCH_PRACTICE_SCORES_FAILURE,
      payload: error.message
    });
    return [];
  }
};

// Add fetchAptitudePracticeScores action creator
export const fetchAptitudePracticeScores = (email) => async (dispatch) => {
  console.log('🎯 fetchAptitudePracticeScores FUNCTION CALLED!')
  console.log('🎯 Email parameter received:', email)
  console.log('🎯 Using test email: rajendran.k+0312@pluginlive.com')

  try {
    // Use the test email for now
    const testEmail = 'rajendran.k+0312@pluginlive.com'

    console.log('=== FETCH APTITUDE PRACTICE SCORES ACTION ===')
    console.log('Fetching aptitude scores for email:', testEmail)

    dispatch({ type: FETCH_APTITUDE_PRACTICE_SCORES_REQUEST });

    // Use the correct endpoint from Assessment-React
    console.log('🔍 Using correct endpoint: students/assessments/getAllAptitudeScoresOfStudent?primaryEmail=' + encodeURIComponent(email))
    const response = await studentRequest.get(
      `students/assessments/getAllAptitudeScoresOfStudent?primaryEmail=${encodeURIComponent(email)}`
    );
    const result = response.data;

    console.log('🔍 Aptitude API Response:', result)
    console.log('🔍 Aptitude Response data:', result.data)
    console.log('🔍 Aptitude Response message:', result.message)

    if (result.message === 'success') {
      console.log('✅ Success - dispatching aptitude data:', result.data)

      // Handle empty object response by converting to empty array (same as Assessment-React)
      let aptitudeData = result.data
      if (!aptitudeData || (typeof aptitudeData === 'object' && !Array.isArray(aptitudeData) && Object.keys(aptitudeData).length === 0)) {
        console.log('🔍 Aptitude data is empty object, converting to empty array')
        aptitudeData = []
      } else if (!Array.isArray(aptitudeData)) {
        console.warn('Aptitude practice scores data is not an array, converting to array')
        aptitudeData = []
      }

      // Extract dayZero data if available
      const dayZero = result.dayZero || null
      console.log('🎯 Aptitude dayZero data extracted:', dayZero)

      const payload = {
        data: aptitudeData,
        dayZero: dayZero
      }

      dispatch({
        type: FETCH_APTITUDE_PRACTICE_SCORES_SUCCESS,
        payload: payload
      });
      return payload;
    } else {
      console.error('❌ API returned non-success message:', result.error || 'Unknown error');
      throw new Error(result.error || 'Failed to fetch aptitude practice scores');
    }
  } catch (error) {
    console.error('❌ Aptitude practice scores exception:', error);
    console.error('❌ Error status:', error.response?.status);
    console.error('❌ Error data:', error.response?.data);
    console.error('❌ Error config URL:', error.config?.url);

    // If we get 404, try alternative endpoints
    if (error.response?.status === 404) {
      console.log('🔄 404 error, trying alternative endpoints...')
      return await tryAlternativeAptitudeEndpoints(dispatch, testEmail);
    }

    dispatch({
      type: FETCH_APTITUDE_PRACTICE_SCORES_FAILURE,
      payload: error.message
    });
    return [];
  }
};
export const fetchPracticeScores2 = (email) => async (dispatch) => {
  try {
    console.log('🎯 fetchPracticeScores2 FUNCTION CALLED!')
    console.log('🎯 Email parameter received:', email)
    console.log('🎯 Using test email: rajendran.k+0312@pluginlive.com')

    // Use the test email for now
    const testEmail = 'rajendran.k+0312@pluginlive.com'

    dispatch({ type: FETCH_PRACTICE_SCORES2_REQUEST });

    console.log('🔍 Using endpoint: students/practice-scores/' + encodeURIComponent(email))
    const response = await studentRequest.get(`students/practice-scores/${encodeURIComponent(email)}?is_practice=false`);
    const result = response.data;

    console.log('🔍 Practice API Response:', result)
    console.log('🔍 Practice Response data:', result.data)
    console.log('🔍 Practice Response message:', result.message)

    if (result.message === 'success') {
      console.log('✅ Success - dispatching practice data:', result.data)

      // Handle empty object response by converting to empty array (same as Assessment-React)
      let practiceData = result.data
      if (!practiceData || (typeof practiceData === 'object' && !Array.isArray(practiceData) && Object.keys(practiceData).length === 0)) {
        console.log('🔍 Practice data is empty object, converting to empty array')
        practiceData = []
      } else if (!Array.isArray(practiceData)) {
        console.warn('Practice scores data is not an array, converting to array')
        practiceData = []
      }

      // Extract dayZero data if available (Communication uses "dataZero" key)
      const dayZero = result.dataZero || result.dayZero || null
      console.log('🎯 Communication dayZero data extracted:', dayZero)

      const payload = {
        data: practiceData,
        dayZero: dayZero
      }

      dispatch({
        type: FETCH_PRACTICE_SCORES2_SUCCESS,
        payload: payload
      });
      return payload;
    } else {
      console.error('❌ API returned non-success message:', result.error || 'Unknown error');
      throw new Error(result.error || 'Failed to fetch practice scores');
    }
  } catch (error) {
    console.error('❌ Practice scores exception:', error);
    console.error('❌ Error status:', error.response?.status);
    console.error('❌ Error data:', error.response?.data);
    console.error('❌ Error config URL:', error.config?.url);

    // If we get 404, try alternative endpoints
    if (error.response?.status === 404) {
      console.log('🔄 404 error, trying alternative practice endpoints...')
      return await tryAlternativePracticeEndpoints(dispatch, testEmail);
    }

    dispatch({
      type: FETCH_PRACTICE_SCORES2_FAILURE,
      payload: error.message
    });
    return [];
  }
};

// Add fetchAptitudePracticeScores action creator
export const fetchAptitudePracticeScores2 = (email) => async (dispatch) => {
  console.log('🎯 fetchAptitudePracticeScores2 FUNCTION CALLED!')
  console.log('🎯 Email parameter received:', email)
  console.log('🎯 Using test email: rajendran.k+0312@pluginlive.com')

  try {
    // Use the test email for now
    const testEmail = 'rajendran.k+0312@pluginlive.com'

    console.log('=== FETCH APTITUDE ASSESSMENT SCORES ACTION ===')
    console.log('Fetching aptitude scores for email:', testEmail)

    dispatch({ type: FETCH_APTITUDE_PRACTICE_SCORES2_REQUEST });

    // Use the correct endpoint from Assessment-React
    console.log('🔍 Using correct endpoint: students/assessments/getAllAptitudeScoresOfStudent?primaryEmail=' + encodeURIComponent(email))
    const response = await studentRequest.get(
      `students/assessments/getAllAptitudeScoresOfStudent?primaryEmail=${encodeURIComponent(email)}&is_practice=false`
    );
    const result = response.data;

    console.log('🔍 Aptitude API Response:', result)
    console.log('🔍 Aptitude Response data:', result.data)
    console.log('🔍 Aptitude Response message:', result.message)

    if (result.message === 'success') {
      console.log('✅ Success - dispatching aptitude data:', result.data)

      // Handle empty object response by converting to empty array (same as Assessment-React)
      let aptitudeData = result.data
      if (!aptitudeData || (typeof aptitudeData === 'object' && !Array.isArray(aptitudeData) && Object.keys(aptitudeData).length === 0)) {
        console.log('🔍 Aptitude data is empty object, converting to empty array')
        aptitudeData = []
      } else if (!Array.isArray(aptitudeData)) {
        console.warn('Aptitude practice scores data is not an array, converting to array')
        aptitudeData = []
      }

      // Extract dayZero data if available
      const dayZero = result.dayZero || null
      console.log('🎯 Aptitude dayZero data extracted:', dayZero)

      const payload = {
        data: aptitudeData,
        dayZero: dayZero
      }

      dispatch({
        type: FETCH_APTITUDE_PRACTICE_SCORES2_SUCCESS,
        payload: payload
      });
      return payload;
    } else {
      console.error('❌ API returned non-success message:', result.error || 'Unknown error');
      throw new Error(result.error || 'Failed to fetch aptitude practice scores');
    }
  } catch (error) {
    console.error('❌ Aptitude practice scores exception:', error);
    console.error('❌ Error status:', error.response?.status);
    console.error('❌ Error data:', error.response?.data);
    console.error('❌ Error config URL:', error.config?.url);

    // If we get 404, try alternative endpoints
    if (error.response?.status === 404) {
      console.log('🔄 404 error, trying alternative endpoints...')
      return await tryAlternativeAptitudeEndpoints(dispatch, testEmail);
    }

    dispatch({
      type: FETCH_APTITUDE_PRACTICE_SCORES2_FAILURE,
      payload: error.message
    });
    return [];
  }
};





// Helper function to try alternative endpoints for aptitude practice scores
const tryAlternativeAptitudeEndpoints = async (dispatch, email) => {
  const alternativeEndpoints = [
    `students/assessments/getAllAptitudeScoresOfStudent?primaryEmail=${encodeURIComponent(email)}`, // Primary correct endpoint
    `students/practice-scores/${encodeURIComponent(email)}?type=aptitude`,
    `students/getAptitudePracticeScores/${encodeURIComponent(email)}`,
    `students/aptitude-practice-scores/${encodeURIComponent(email)}`,
    `students/practice-scores-aptitude?email=${encodeURIComponent(email)}`,
    `students/practice-scores?email=${encodeURIComponent(email)}&type=aptitude`
  ];

  for (const endpoint of alternativeEndpoints) {
    try {
      console.log(`🔄 Trying alternative aptitude endpoint: ${endpoint}`)
      const response = await studentRequest.get(endpoint);
      const result = response.data;

      console.log(`✅ Alternative aptitude endpoint success: ${endpoint}`, result)

      if (result.message === 'success') {
        // Handle data same as main function
        let aptitudeData = result.data
        if (!aptitudeData || (typeof aptitudeData === 'object' && !Array.isArray(aptitudeData) && Object.keys(aptitudeData).length === 0)) {
          aptitudeData = []
        } else if (!Array.isArray(aptitudeData)) {
          aptitudeData = []
        }

        dispatch({
          type: FETCH_APTITUDE_PRACTICE_SCORES_SUCCESS,
          payload: aptitudeData
        });
        return aptitudeData;
      }
    } catch (altError) {
      console.log(`❌ Alternative aptitude endpoint failed: ${endpoint} - Status: ${altError.response?.status}`)
    }
  }

  console.error('❌ All alternative aptitude endpoints failed')
  return [];
}

// Helper function to try alternative endpoints for practice scores
const tryAlternativePracticeEndpoints = async (dispatch, email) => {
  const alternativeEndpoints = [
    `students/getPracticeScores/${encodeURIComponent(email)}`,
    `students/practice-scores?email=${encodeURIComponent(email)}`,
    `students/communication-practice-scores/${encodeURIComponent(email)}`,
    `students/practice-scores/${encodeURIComponent(email)}?type=communication`,
    `students/assessments/getAllCommunicationScoresOfStudent?primaryEmail=${encodeURIComponent(email)}`
  ];

  for (const endpoint of alternativeEndpoints) {
    try {
      console.log(`🔄 Trying alternative practice endpoint: ${endpoint}`)
      const response = await studentRequest.get(endpoint);
      const result = response.data;

      console.log(`✅ Alternative practice endpoint success: ${endpoint}`, result)

      if (result.message === 'success') {
        // Handle data same as main function
        let practiceData = result.data
        if (!practiceData || (typeof practiceData === 'object' && !Array.isArray(practiceData) && Object.keys(practiceData).length === 0)) {
          practiceData = []
        } else if (!Array.isArray(practiceData)) {
          practiceData = []
        }

        dispatch({
          type: FETCH_PRACTICE_SCORES_SUCCESS,
          payload: practiceData
        });
        return practiceData;
      }
    } catch (altError) {
      console.log(`❌ Alternative practice endpoint failed: ${endpoint} - Status: ${altError.response?.status}`)
    }
  }

  console.error('❌ All alternative practice endpoints failed')
  return [];
}

// Add this new action creator at the end of the file
export const fetchCompletedPracticeAssessments = (studentId, isPractice = true) => async (dispatch) => {
  try {
    console.log('=== FETCH COMPLETED PRACTICE ASSESSMENTS ACTION ===')
    console.log('Fetching assessments for studentId:', studentId)
    console.log('isPractice:', isPractice)

    dispatch({ type: FETCH_COMPLETED_PRACTICE_ASSESSMENTS_REQUEST })

    const response = await studentRequest.get(
      `students/getCompletedAssessment/${studentId}?isPractice=${isPractice}`
    )

    const result = response.data
    console.log('🔍 Completed Practice API Response:', result)

    if (result.message === 'success') {
      console.log('✅ Success - dispatching completed practice data:', result.data)
      dispatch({
        type: FETCH_COMPLETED_PRACTICE_ASSESSMENTS_SUCCESS,
        payload: result.data
      })
      return result.data
    } else {
      console.error('❌ API returned non-success message:', result.error || 'Unknown error')
      throw new Error(result.error || 'Failed to fetch completed practice assessments')
    }
  } catch (error) {
    console.error('❌ Completed practice assessments exception:', error)
    console.error('Error details:', error.response?.data)
    dispatch({
      type: FETCH_COMPLETED_PRACTICE_ASSESSMENTS_FAILURE,
      payload: error.message || 'Failed to fetch completed practice assessments'
    })
    throw error
  }
}



// In your actions file (modify the fetchStudentAssessments action)
export const fetchStudentAssessments = (studentId, isPractice = false) => {
  return async (dispatch, getState) => { // Add getState to access Redux state
    dispatch({ type: 'FETCH_STUDENT_ASSESSMENTS_REQUEST' })

    try {
      // console.log('=== FETCH STUDENT ASSESSMENTS ACTION ===')
      // console.log('Fetching assessments for studentId:', studentId)
      // console.log('isPractice:', isPractice)

      // Get instituteCampusId from auth reducer
      const state = getState()
      const instituteCampusId = state.auth?.instituteCampusId || state.authentication?.instituteCampusId // Adjust path based on your auth reducer structure

      // console.log('instituteCampusId from auth:', instituteCampusId)

      // Build query parameters
      const queryParams = {
        isPractice: isPractice,
      }

      // Add instituteId parameter if instituteCampusId exists
      if (instituteCampusId) {
        queryParams.instituteId = instituteCampusId
      }

      // Convert to query string
      const queryString = new URLSearchParams(queryParams).toString()

      const response = await studentRequest.get(
        `students/getCompletedAssessmentAll/${studentId}?${queryString}`
      )

      const data = response.data
      // console.log('API Response:', data)

      if (data.message === 'success') {
        // console.log('✅ Success - dispatching data:', data.data)
        dispatch({
          type: 'FETCH_STUDENT_ASSESSMENTS_SUCCESS',
          payload: data.data
        })
        return data.data
      } else {
        throw new Error(data.error || 'Failed to fetch student assessments')
      }
    } catch (error) {
      console.error('❌ Student assessments exception:', error)
      console.error('Error details:', error.response?.data)

      // Handle different error types
      let errorMessage = 'Failed to fetch student assessments'
      if (error.response?.status === 500) {
        errorMessage = 'Server error: Unable to load student assessment details. Please try again later.'
      } else if (error.response?.status === 404) {
        errorMessage = 'Student assessment data not found.'
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied: You do not have permission to view this student\'s data.'
      }

      dispatch({
        type: 'FETCH_STUDENT_ASSESSMENTS_FAILURE',
        payload: errorMessage
      })

      // Don't throw error to prevent app crash - just log it
      console.warn('Student assessment fetch failed, continuing without crash:', errorMessage)
      return null
    }
  }
}

export const fetchLastAssessment = (primaryEmail) => async (dispatch) => {
  try {
    console.log('=== FETCH LAST ASSESSMENT ACTION ===')
    console.log('Fetching last assessment for email:', primaryEmail)

    dispatch({ type: FETCH_LAST_ASSESSMENT_REQUEST })

    const response = await studentRequest.get(
      `students/getLastGivenAssessment?primaryEmail=${encodeURIComponent(primaryEmail)}&is_practice=false`
    )

    const result = response.data
    console.log('Last Assessment API Response:', result)

    if (result.message === 'success') {
      console.log('✅ Success - dispatching last assessment data:', result.data)
      dispatch({
        type: FETCH_LAST_ASSESSMENT_SUCCESS,
        payload: result.data
      })
      return result.data
    } else {
      console.error('❌ API returned non-success message:', result.error || 'Unknown error')
      throw new Error(result.error || 'Failed to fetch last assessment')
    }
  } catch (error) {
    console.error('❌ Last assessment exception:', error)
    console.error('Error details:', error.response?.data)
    dispatch({
      type: FETCH_LAST_ASSESSMENT_FAILURE,
      payload: error.message || 'Failed to fetch last assessment'
    })
    throw error
  }
}

export const fetchLastPractice = (primaryEmail) => async (dispatch) => {
  try {
    console.log('=== FETCH LAST PRACTICE ACTION ===')
    console.log('Fetching last practice for email:', primaryEmail)

    dispatch({ type: FETCH_LAST_PRACTICE_REQUEST })

    const response = await studentRequest.get(
      `students/getLastGivenAssessment?primaryEmail=${encodeURIComponent(primaryEmail)}&is_practice=true`
    )

    const result = response.data
    console.log('Last Practice API Response:', result)

    if (result.message === 'success') {
      console.log('✅ Success - dispatching last practice data:', result.data)
      dispatch({
        type: FETCH_LAST_PRACTICE_SUCCESS,
        payload: result.data
      })
      return result.data
    } else {
      console.error('❌ API returned non-success message:', result.error || 'Unknown error')
      throw new Error(result.error || 'Failed to fetch last practice')
    }
  } catch (error) {
    console.error('❌ Last practice exception:', error)
    console.error('Error details:', error.response?.data)
    dispatch({
      type: FETCH_LAST_PRACTICE_FAILURE,
      payload: error.message || 'Failed to fetch last practice'
    })
    throw error
  }
}

// Add this new action creator at the end of the file
export const fetchCEFRLevel = (primaryEmail) => async (dispatch) => {
  try {
    console.log('=== FETCH CEFR LEVEL ACTION ===')
    console.log('Fetching CEFR level for email:', primaryEmail)

    dispatch({ type: FETCH_CEFR_LEVEL_REQUEST })

    const response = await studentRequest.get(
      `students/getMaxResultingCEFR/${encodeURIComponent(primaryEmail)}`
    )

    const result = response.data
    console.log('CEFR Level API Response:', result)

    if (result.message === 'success') {
      console.log('✅ Success - dispatching CEFR data:', result.data)
      dispatch({
        type: FETCH_CEFR_LEVEL_SUCCESS,
        payload: result.data
      })
      return result.data
    } else {
      console.error('❌ API returned non-success message:', result.error || 'Unknown error')
      throw new Error(result.error || 'Failed to fetch CEFR level')
    }
  } catch (error) {
    console.error('❌ CEFR level exception:', error)
    console.error('Error details:', error.response?.data)
    dispatch({
      type: FETCH_CEFR_LEVEL_FAILURE,
      payload: error.message || 'Failed to fetch CEFR level'
    })
    throw error
  }
}

// Add this new action
export const fetchAssessmentScores = (studentEmail) => {
  return async (dispatch, getState) => {
    dispatch({ type: 'FETCH_ASSESSMENT_SCORES_REQUEST' })

    try {
      // console.log('=== FETCH ASSESSMENT SCORES ACTION ===')
      // console.log('Fetching assessment scores for studentEmail:', studentEmail)

      const response = await studentRequest.get(
        `students/assessment-scores/${encodeURIComponent(studentEmail)}`
      )

      const data = response.data
      // console.log('Assessment Scores API Response:', data)

      if (data.message === 'success') {
        // console.log('✅ Success - dispatching assessment scores data:', data.data)
        dispatch({
          type: 'FETCH_ASSESSMENT_SCORES_SUCCESS',
          payload: data.data
        })
        return data.data
      } else {
        throw new Error(data.error || 'Failed to fetch assessment scores')
      }
    } catch (error) {
      console.error('❌ Assessment scores exception:', error)
      console.error('Error details:', error.response?.data)
      dispatch({
        type: 'FETCH_ASSESSMENT_SCORES_FAILURE',
        payload: error.message || 'Failed to fetch assessment scores'
      })
      throw error
    }
  }
}

// Add this fetch reports action - make sure to add this after the existing actions
export const fetchReports = (assessmentAssignedIds = [], studentId = null) => async (dispatch, getState) => {
  try {
    console.log("🔍 FETCH REPORTS START")
    dispatch(fetchReportsRequest())

    const state = getState()
    // Use passed studentId or try to get from state
    const finalStudentId = studentId || state.auth?.studentId || state.auth?.user?.id

    console.log("🔍 FETCH REPORTS - Student ID:", finalStudentId)
    console.log("🔍 FETCH REPORTS - Passed Student ID:", studentId)

    if (!finalStudentId) {
      throw new Error("Student ID not found")
    }

    // Use provided assessment IDs
    let idsToFetch = assessmentAssignedIds

    if (!idsToFetch || idsToFetch.length === 0) {
      console.log("🔍 FETCH REPORTS - No assessment IDs found, dispatching empty array")
      dispatch(fetchReportsSuccess([]))
      return []
    }

    // Prepare the payload with assessment assigned IDs
    const payload = {
      assessment_assigned_ids: idsToFetch,
      student_id: finalStudentId,
    }

    console.log("🔍 FETCH REPORTS - API payload:", payload)

    // Make the API call with the correct payload
    const response = await studentRequest.post("students/getAssessmentReport", payload)

    console.log("🔍 FETCH REPORTS - API response:", response.data)

    if (response.data && response.data.message === "success") {
      const reportsData = response.data.data || []
      console.log("🔍 FETCH REPORTS - Success, dispatching reports:", reportsData.length, "reports")
      dispatch(fetchReportsSuccess(reportsData))
      return reportsData
    } else {
      throw new Error(response.data?.message || "Failed to fetch reports")
    }
  } catch (error) {
    console.error("❌ FETCH REPORTS ERROR:", error)
    dispatch(fetchReportsFailure(error.message))
    throw error
  }
}







export const fetchScoreInfo = (primaryEmail) => {
  return async (dispatch, getState) => {
    dispatch({ type: 'FETCH_SCORE_INFO_REQUEST' })

    try {
      console.log('=== FETCH SCORE INFO ACTION ===')
      console.log('Fetching score info for primaryEmail:', primaryEmail)

      const response = await studentRequest.get(
        `students/getScoreInfo?primaryEmail=${encodeURIComponent(primaryEmail)}`
      )

      const data = response.data
      console.log('Score Info API Response:', data)

      if (data.message === 'success') {
        console.log('✅ Success - dispatching score info data:', data.data)
        dispatch({
          type: 'FETCH_SCORE_INFO_SUCCESS',
          payload: data.data
        })
        return data.data
      } else {
        throw new Error(data.error || 'Failed to fetch score info')
      }
    } catch (error) {
      console.error('❌ Score info exception:', error)
      console.error('Error details:', error.response?.data)
      dispatch({
        type: 'FETCH_SCORE_INFO_FAILURE',
        payload: error.message || 'Failed to fetch score info'
      })
      throw error
    }
  }
}



export const fetchMediaKeys = (assessmentAssignedId) => async (dispatch) => {
  dispatch({ type: FETCH_MEDIA_KEYS_REQUEST })

  try {
    const response = await fetch(
      `${process.env.ADMIN_API_URL}/assessment/getAudioVideoProctoring?assessmentAssignedId=${assessmentAssignedId}`
    )

    if (!response.ok) throw new Error(`Failed to fetch keys (${response.status})`)

    const data = await response.json()

    if (!data.data?.rows || !Array.isArray(data.data.rows) || data.data.rows.length === 0) {
      dispatch({
        type: FETCH_MEDIA_KEYS_SUCCESS,
        payload: {
          mediaFiles: [],
          audioAvailable: false,
          videoAvailable: false
        }
      })
      return
    }

    const rows = data.data.rows
    const audioPatterns = ['audio', 'paragraph_reading', 'paragraph', 'reading']
    const videoPatterns = ['video', 'video_respose', 'webcam']

    const audioKey = rows.find(r =>
      audioPatterns.some(pattern =>
        r.object_key.toLowerCase().includes(pattern.toLowerCase())
      )
    )?.object_key

    const videoKey = rows.find(r =>
      videoPatterns.some(pattern =>
        r.object_key.toLowerCase().includes(pattern.toLowerCase())
      )
    )?.object_key

    const files = []
    if (audioKey) files.push({ filename: audioKey, type: 'audio' })
    if (videoKey) files.push({ filename: videoKey, type: 'video' })

    dispatch({
      type: FETCH_MEDIA_KEYS_SUCCESS,
      payload: {
        mediaFiles: files,
        audioAvailable: !!audioKey,
        videoAvailable: !!videoKey
      }
    })

  } catch (error) {
    dispatch({
      type: FETCH_MEDIA_KEYS_FAILURE,
      payload: error.message
    })
  }
}

export const fetchMediaUrls = (files) => async (dispatch) => {
  dispatch({ type: FETCH_MEDIA_URLS_REQUEST })

  try {
    const response = await fetch(`${process.env.STD_API_URL}students/assessments/getMediaUrls`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ files })
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to fetch media URLs: ${errorText}`)
    }

    const data = await response.json()

    if (!data.data?.urls) {
      throw new Error('Invalid response format: missing data.urls')
    }

    const urls = data.data.urls
    const audioFile = files.find(f => f.type === 'audio')
    const videoFile = files.find(f => f.type === 'video')

    const audioUrl = audioFile ? urls[audioFile.filename] || null : null
    const videoUrl = videoFile ? urls[videoFile.filename] || null : null

    dispatch({
      type: FETCH_MEDIA_URLS_SUCCESS,
      payload: {
        audio: audioUrl,
        video: videoUrl
      }
    })

  } catch (error) {
    dispatch({
      type: FETCH_MEDIA_URLS_FAILURE,
      payload: error.message
    })
  }
}

export const checkReportAvailability = (assessmentAssignedId, studentNumber) => async (dispatch) => {
  dispatch({ type: CHECK_REPORT_AVAILABILITY_REQUEST })

  try {
    // Use studentRequest for authenticated call
    const response = await studentRequest.get(
      `students/assessments/checkReportAvailability?assessmentAssignedId=${assessmentAssignedId}&studentId=${studentNumber}`
    )

    const data = response.data

    dispatch({
      type: CHECK_REPORT_AVAILABILITY_SUCCESS,
      payload: {
        reportAvailable: data?.data?.available || false
      }
    })

  } catch (error) {
    dispatch({
      type: CHECK_REPORT_AVAILABILITY_FAILURE,
      payload: error.message
    })
  }
}

export const fetchAptitudeMetrics = (studentId) => async (dispatch, getState) => {
  dispatch({ type: FETCH_APTITUDE_METRICS_REQUEST })

  try {
    // Your API call to fetch aptitude specific data
    const response = await fetch(`/api/students/${studentId}/aptitude-metrics`)
    const data = await response.json()

    console.log('Aptitude Metrics API Response:', data)

    dispatch({
      type: FETCH_APTITUDE_METRICS_SUCCESS,
      payload: data
    })
  } catch (error) {
    dispatch({
      type: FETCH_APTITUDE_METRICS_FAILURE,
      payload: error.message
    })
  }
}

// Chart API Action Creators
export const fetchCefrChartData = (assessmentId, passingYear = '', isDiagnosis = false, scheduleId = null, attemptNumber = null) => async dispatch => {
  console.log('Fetching CEFR Chart Data:', { assessmentId, passingYear, isDiagnosis, scheduleId, attemptNumber })
  try {
    // Build query parameters
    const params = new URLSearchParams({
      assessmentInstituteMapId: assessmentId,
      ...(passingYear ? { passingYear } : {}),
      is_diagnosis: isDiagnosis
    });

    // Make the API request
    console.log('Making request to:', `/assessment/getPiChartDataForCefr?${params.toString()}`);

    const response = await studentRequest.get(
      `/assessment/getPiChartDataForCefr?${params.toString()}`
    );

    console.log('CEFR API Response Status:', response.status);
    console.log('CEFR API Response Data:', response.data);

    // Check if the response is valid JSON and has the expected structure
    if (response.data && typeof response.data === 'object') {
      if (response.data.message === 'Success' || response.data.message === 'success') {
        console.log('CEFR API Success - Data:', response.data.data);
        return response.data.data;
      } else if (response.data.message) {
        console.warn('CEFR API Warning:', response.data.message);
        // If it's a "no data found" scenario, return empty dataset
        if (response.data.message.includes('No CEFR data found')) {
          console.warn('⚠️ No CEFR data available for this assessment - returning empty dataset');
          return {
            A1: { count: 0, percentage: 0 },
            A2: { count: 0, percentage: 0 },
            B1: { count: 0, percentage: 0 },
            B2: { count: 0, percentage: 0 },
            C1: { count: 0, percentage: 0 },
            C2: { count: 0, percentage: 0 },
            totalStudentCount: 0
          };
        }
        throw new Error(response.data.message);
      } else if (response.data.error) {
        console.error('CEFR API Error:', response.data.error);
        throw new Error(response.data.error);
      }
    }

    // If we get here, the response format is unexpected
    console.error('Unexpected response format from CEFR API');
    throw new Error('Unexpected response format from server');

  } catch (error) {
    console.error('CEFR API Error:', error);

    // Log additional error details if available
    if (error.response) {
      console.error('Error Response Status:', error.response.status);
      console.error('Error Response Headers:', error.response.headers);
      console.error('Error Response Data:', error.response.data);

      // If it's an auth error, clear tokens and redirect
      if (error.response.status === 401) {
        console.warn('Authentication error - clearing tokens');
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        window.location.href = '/login';
      }
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }

    // Re-throw the error to be handled by the caller
    throw error;
  }
}

export const fetchConsistencyData = (assessmentId, assessmentType = 'communication', instituteId = '', passingYear = '', isDiagnosis = false, scheduleId = null, attemptNumber = null) => async (dispatch) => {
  try {
    console.log('=== Consistency API Call ===');
    console.log('Assessment-specific ID for Consistency:', assessmentId);
    console.log('Assessment Type:', assessmentType);
    console.log('Institute ID:', instituteId);
    console.log('Passing Year:', passingYear);
    console.log('Is Diagnosis:', isDiagnosis);

    // Validate input parameters
    if (!assessmentId) {
      throw new Error('Assessment ID is required');
    }

    if (!instituteId) {
      throw new Error('Institute ID is required');
    }

    if (!['communication', 'aptitude'].includes(assessmentType.toLowerCase())) {
      console.warn(`Invalid assessment type: ${assessmentType}. Defaulting to 'communication'`);
      assessmentType = 'communication';
    }

    // Build query parameters
    const params = new URLSearchParams({
      assessmentInstituteMapID: assessmentId,
      assessmentType: assessmentType.toLowerCase(),
      instituteId,
      ...(passingYear ? { passingYear } : {}),
      is_diagnosis: isDiagnosis
    });

    const url = `/assessment/getConsistencyData?${params.toString()}`;
    console.log('API URL:', url);

    // Make the API request
    const response = await studentRequest.get(url);

    console.log('=== Consistency API Response ===');
    console.log('Status:', response.status);
    console.log('Data type:', typeof response.data);
    console.log('Data:', response.data);

    // Handle different response statuses
    if (response.data && response.data.message?.toLowerCase() === 'success') {
      console.log('Consistency API Success - Data:', response.data.data);
      return response.data.data;
    } else if (response.data && response.data.message) {
      // Handle API-specific error messages
      const errorMsg = response.data.message || 'Unknown error from server';
      console.warn('Consistency API Warning:', errorMsg);
      throw new Error(`API Error: ${errorMsg}`);
    } else {
      // Handle unexpected response format
      console.error('Unexpected response format:', response.data);
      throw new Error('Unexpected response format from server');
    }

  } catch (error) {
    console.error('Consistency API Error:', {
      message: error.message,
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method,
      response: error.response?.data
    });

    // Enhance error message with more context
    let errorMessage = 'Failed to load consistency data';

    if (error.code === 'ECONNABORTED') {
      errorMessage = 'Request timed out. Please check your connection and try again.';
    } else if (!navigator.onLine) {
      errorMessage = 'Network connection lost. Please check your internet connection.';
    } else if (error.response) {
      // Server responded with error status
      if (error.response.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.response.data?.message) {
        errorMessage = error.response.data.message;
      }
    }

    // Create a new error with the enhanced message
    const enhancedError = new Error(errorMessage);
    enhancedError.originalError = error;
    enhancedError.status = error.response?.status;

    // Dispatch error to Redux store if needed
    dispatch({
      type: 'API_ERROR',
      payload: {
        action: 'fetchConsistencyData',
        error: enhancedError.message,
        timestamp: new Date().toISOString()
      }
    });

    throw enhancedError;
  }
}

export const fetchAptitudeAssessmentGroups = (assessmentId, passingYear, scheduleId = null) => async (dispatch) => {
  try {
    console.log('=== Aptitude Assessment Groups API Call ===');
    console.log('Assessment ID:', assessmentId);
    console.log('Passing Year:', passingYear);

    // Log the token being used for the request
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    console.log('Using token:', token ? 'Token exists' : 'No token found');

    // Make the API request
    const url = `/assessment/getAptitudeAssessmentGroups?assessmentInstituteMapID=${assessmentId}${passingYear ? `&passingYear=${passingYear}` : ''}`;
    console.log('Making request to:', url);

    const response = await adminRequest.get(
      url,
      {
        validateStatus: function (status) {
          return status >= 200 && status < 500; // Resolve only if the status code is less than 500
        }
      }
    );

    if (response.status === 200 && response.data && response.data.message === 'Success') {
      console.log('Successfully fetched aptitude assessment groups:', response.data.data);
      return response.data.data;
    } else {
      console.error('Failed to fetch aptitude assessment groups:', response.data);
      throw new Error(response.data?.message || 'Failed to fetch aptitude assessment groups');
    }
  } catch (error) {
    console.error('Error in fetchAptitudeAssessmentGroups:', error);
    throw error;
  }
};

// Fetch Communication Assessment Groups with Normalized Scores (for line chart)
export const fetchCommunicationAssessmentGroups = (assessmentId, passingYear, scheduleId = null) => async (dispatch) => {
  try {
    console.log('=== Fetching Communication Assessment Groups ===');
    console.log('Assessment ID:', assessmentId);
    console.log('Passing Year:', passingYear);

    // Log the token being used for the request
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    console.log('Using token:', token ? 'Token exists' : 'No token found');

    // Make the API request
    const url = `/assessment/getCommunicationAssessmentGroupsWithNormalizedScores?assessmentInstituteMapID=${assessmentId}${passingYear ? `&passingYear=${passingYear}` : ''}`;
    console.log('Making request to:', url);

    const response = await adminRequest.get(
      url,
      {
        validateStatus: function (status) {
          return status >= 200 && status < 500; // Resolve only if the status code is less than 500
        }
      }
    );

    if (response.status === 200 && response.data && response.data.message === 'Success') {
      console.log('Successfully fetched communication assessment groups:', response.data.data);
      return response.data.data;
    }
    // Graceful handling for non-200 (including 4xx and 500 per validateStatus)
    console.warn('[fetchCommunicationAssessmentGroups] Non-success response:', {
      status: response.status,
      data: response.data,
    });
    // Return empty dataset to keep UI functional instead of throwing
    return [];
  } catch (error) {
    console.error('Error in fetchCommunicationAssessmentGroups, returning []:', error);
    return [];
  }
};

// Fetch Aptitude Pie Chart Data
export const fetchAptitudePieChartData = (assessmentId, passingYear = '', isDiagnosis = false, scheduleId = null, attemptNumber = null) => async dispatch => {
  console.log('Fetching Aptitude Pie Chart Data:', { assessmentId, passingYear, isDiagnosis })
  try {
    // Build query parameters
    const params = new URLSearchParams({
      assessmentInstituteMapId: assessmentId,
      ...(passingYear ? { passingYear } : {}),
      is_diagnosis: isDiagnosis
    });

    // Use the specific endpoint requested: /assessment/getPiChartDataForAptitude
    const apiPath = `/assessment/getPiChartDataForAptitude?${params.toString()}`
    console.log('Making request to:', apiPath);

    const response = await studentRequest.get(
      apiPath
    );

    if (response.status === 200 && response.data && (response.data.message === 'Success' || response.data.message === 'success')) {
      console.log('[Aptitude Pie] Endpoint success');
      return response.data.data;
    }

    console.error('[Aptitude Pie] Endpoint failed:', {
      status: response.status,
      data: response.data,
    })
    throw new Error(response.data?.message || 'Failed to fetch aptitude pie chart data')
  } catch (error) {
    console.error('Error in fetchAptitudePieChartData:', error);
    throw error;
  }
};

// Combined chart data fetcher using Promise.all
export const fetchAssessmentChartsData = (assessmentInstituteMapID, assessmentType = 'communication', instituteId = '', passingYear = '', isDiagnosis = false, scheduleId = null, attemptNumber = null) => async dispatch => {
  // Validate input
  if (!assessmentInstituteMapID) {
    const errorMsg = 'Assessment Institute Map ID is required';
    console.error(errorMsg);
    dispatch({
      type: FETCH_ASSESSMENT_CHARTS_FAILURE,
      error: errorMsg,
      timestamp: new Date().toISOString()
    });
    throw new Error(errorMsg);
  }

  // Validate assessment type
  if (!['communication', 'aptitude', 'hinglish'].includes(assessmentType.toLowerCase())) {
    console.warn(`Invalid assessment type: ${assessmentType}. Defaulting to 'communication'`);
    assessmentType = 'communication';
  }

  console.log('=== [fetchAssessmentChartsData] Starting data fetch ===');
  console.log('- Assessment ID:', assessmentInstituteMapID);
  console.log('- Assessment Type:', assessmentType);
  console.log('- Institute ID:', instituteId);
  console.log('- Passing Year:', passingYear);
  console.log('- Is Diagnosis:', isDiagnosis);

  dispatch({
    type: FETCH_ASSESSMENT_CHARTS_REQUEST,
    meta: { assessmentId: assessmentInstituteMapID, assessmentType, isDiagnosis }
  });

  try {
    console.log('=== [fetchAssessmentChartsData] Fetching all chart data in parallel');

    // Test backend connectivity first (use same base as authRequest)
    try {
      console.log('🔍 Testing backend connectivity (2000) ...')
      const health = await authRequest.get('/health')
      console.log('✅ Backend connectivity test:', health.status)
    } catch (connectError) {
      console.error('❌ Backend connectivity test failed:', connectError?.message || connectError)
      try {
        const base = (authRequest?.defaults?.baseURL || '').toString()
        console.error(`❌ Expected API base from env: ${base} (authRequest)`)
      } catch (_) {
        // no-op
      }
    }

    // Log the start time for performance monitoring
    const startTime = Date.now();

    // Make all API calls in parallel with proper error handling
    const isCommunicationLike = ['communication', 'hinglish'].includes(assessmentType.toLowerCase());
    const [pieChartData, consistencyData, lineChartData] = await Promise.all([
      // Fetch pie chart data based on assessment type (communication and hinglish use CEFR)
      isCommunicationLike
        ? dispatch(fetchCefrChartData(assessmentInstituteMapID, passingYear, isDiagnosis))
          .catch(error => {
            console.error('Error in fetchCefrChartData:', error);
            // Provide empty CEFR pie so UI stays functional
            return { labels: [], datasets: [{ data: [], backgroundColor: [] }] }
          })
        : dispatch(fetchAptitudePieChartData(assessmentInstituteMapID, passingYear, isDiagnosis))
          .catch(error => {
            console.error('Error in fetchAptitudePieChartData:', error);
            // Provide empty Aptitude pie so UI stays functional
            return { labels: [], datasets: [{ data: [], backgroundColor: [] }] }
          }),

      // Fetch consistency data for the specified assessment type
      instituteId
        ? dispatch(fetchConsistencyData(assessmentInstituteMapID, assessmentType, instituteId, passingYear, isDiagnosis))
          .catch(error => {
            console.error('Error in fetchConsistencyData:', error);
            // Return empty consistency data instead of throwing
            return { highConsistency: { count: 0, percentage: 0 }, moderateConsistency: { count: 0, percentage: 0 }, inconsistency: { count: 0, percentage: 0 }, totalStudentCount: 0 };
          })
        : Promise.resolve({ highConsistency: { count: 0, percentage: 0 }, moderateConsistency: { count: 0, percentage: 0 }, inconsistency: { count: 0, percentage: 0 }, totalStudentCount: 0 }),

      // Fetch line chart data based on assessment type - skip if diagnosis
      isDiagnosis
        ? Promise.resolve([])
        : assessmentType.toLowerCase() === 'aptitude'
          ? dispatch(fetchAptitudeAssessmentGroups(assessmentInstituteMapID, passingYear, scheduleId))
            .catch(error => {
              console.error('Error in fetchAptitudeAssessmentGroups:', error);
              throw new Error(`Aptitude Groups Error: ${error.message}`);
            })
          : dispatch(fetchCommunicationAssessmentGroups(assessmentInstituteMapID, passingYear, scheduleId))
            .catch(error => {
              console.error('Error in fetchCommunicationAssessmentGroups:', error);
              throw new Error(`Communication Groups Error: ${error.message}`);
            })
    ]);

    // Log successful data retrieval
    const endTime = Date.now();
    console.log(`=== [fetchAssessmentChartsData] All chart data fetched successfully in ${endTime - startTime}ms`);
    console.log('- Pie Chart Data:', pieChartData ? 'Received' : 'Missing');
    console.log('- Consistency Data:', consistencyData ? 'Received' : 'Missing');
    console.log('- Line Chart Data:',
      lineChartData ? `Received (${Array.isArray(lineChartData) ? lineChartData.length : 'object'} items)` : 'Missing');

    // Prepare the payload with all chart data
    const chartData = {
      // Pie chart data (CEFR for communication/hinglish, Aptitude levels for aptitude)
      cefrData: isCommunicationLike ? pieChartData : null,
      aptitudePieData: assessmentType.toLowerCase() === 'aptitude' ? pieChartData : null,

      // Consistency data (same for both types)
      consistencyData,

      // Line chart data (Communication groups for communication/hinglish, Aptitude groups for aptitude)
      communicationGroupsData: isCommunicationLike ? lineChartData : [],
      aptitudeGroupsData: assessmentType.toLowerCase() === 'aptitude' ? lineChartData : [],

      // Metadata
      assessmentType: assessmentType.toLowerCase(),
      assessmentId: assessmentInstituteMapID,
      lastUpdated: new Date().toISOString()
    };

    // Dispatch success action with the combined data
    dispatch({
      type: FETCH_ASSESSMENT_CHARTS_SUCCESS,
      payload: chartData,
      meta: { assessmentId: assessmentInstituteMapID, assessmentType }
    });

    console.log('=== [fetchAssessmentChartsData] Successfully dispatched chart data to Redux store');

    return chartData;

  } catch (error) {
    // Log detailed error information
    console.error('=== [fetchAssessmentChartsData] Error fetching assessment charts data ===');
    console.error('Error Details:', {
      message: error.message,
      stack: error.stack,
      assessmentId: assessmentInstituteMapID,
      assessmentType,
      response: error.response ? {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      } : 'No response',
      request: error.request ? 'Request made but no response received' : 'No request was made'
    });

    // Enhance error message with more context
    let errorMessage = error.message || 'An unknown error occurred while fetching chart data';

    if (error.code === 'ECONNABORTED') {
      errorMessage = 'Request timed out. Please check your connection and try again.';
    } else if (!navigator.onLine) {
      errorMessage = 'Network connection lost. Please check your internet connection.';
    } else if (error.response) {
      if (error.response.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.response.data?.message) {
        errorMessage = error.response.data.message;
      }
    }

    // Create a new error with the enhanced message
    const enhancedError = new Error(errorMessage);
    enhancedError.originalError = error;
    enhancedError.status = error.response?.status;

    // Dispatch failure action with detailed error information
    dispatch({
      type: FETCH_ASSESSMENT_CHARTS_FAILURE,
      error: enhancedError.message,
      timestamp: new Date().toISOString(),
      meta: {
        assessmentId: assessmentInstituteMapID,
        assessmentType,
        error: enhancedError
      }
    });

    console.error('=== [fetchAssessmentChartsData] Error dispatched to Redux store');

    // Re-throw the enhanced error to allow components to handle it if needed
    throw enhancedError;
  }
}

export const fetchStudentList = (instituteCampusId, pageNumber, searchParams, yearOfPassing) => async (dispatch) => {
  try {
    let pageLimit = '&pageSize=10';
    const response = await studentRequest.get(`/students/getStudentListofInstitute?instituteCampusId=${instituteCampusId}&pageNumber=${pageNumber}&searchParams=${searchParams}&passOutYear=${yearOfPassing}${pageLimit}`);
    console.log('Student List API Response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching student list:', error);
  }
};

export const fetchStudentProgress = (studentId) => async (dispatch) => {
  try {
    const response = await instRequest.get(`/institutes/studentListInfo/getStudentProgress?studentId=${studentId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching student progress:', error);
  }
};

export const fetchAssessmentStatesForInstitute = (params) => async (dispatch) => {
  dispatch({ type: FETCH_ASSESSMENT_STATES_REQUEST })
  try {
    const { instituteId, passingYear } = params
    const response = await studentRequest.get(
      '/students/assessments/getAssessmentStatesForInstitute/' + instituteId,
      {
        params: {
          passingYear
        }
      }
    )
    dispatch({
      type: FETCH_ASSESSMENT_STATES_SUCCESS,
      payload: response.data,
    })
    return response.data
  } catch (error) {
    console.error('Error fetching assessment states:', error)
    dispatch({
      type: FETCH_ASSESSMENT_STATES_FAILURE,
      payload: error.message,
    })
  }
}

export const fetchTpoStudentList = (params) => async (dispatch) => {
  dispatch({ type: FETCH_TPO_STUDENT_LIST_REQUEST })
  try {
    const {
      instituteId,
      pageNumber = 1,
      pageSize = 10,
      searchParams = '',
      passingYear = '',
      domain = [],
      degree = [],
      department = [],
      specialization = [],
      sortBy = { field: 'name', order: 'asc' }
    } = params
    const response = await studentRequest.post(
      `/students/assessments/tpo/student-list/${instituteId}`,
      {
        pageNumber,
        pageSize,
        searchParams,
        passingYear,
        domain: domain.join(','),
        degree: degree.join(','),
        department: department.join(','),
        specialization: specialization.join(','),
        sortBy
      }
    )
    dispatch({
      type: FETCH_TPO_STUDENT_LIST_SUCCESS,
      payload: response.data,
    })
    return response.data
  } catch (error) {
    console.error('Error fetching TPO student list:', error)
    dispatch({
      type: FETCH_TPO_STUDENT_LIST_FAILURE,
      payload: error.message,
    })
  }
}
// Add this new action at the end of the file



export const fetchSpecificAssessmentStudentList =
  (instituteId, params) => async dispatch => {
    dispatch({ type: FETCH_SPECIFIC_ASSESSMENT_STUDENT_LIST_REQUEST })
    try {
      // Use studentRequest.post as the API now uses POST method
      const response = await studentRequest.post(
        `/students/assessments/tpo/specific-assessment-student-list/${instituteId}`,
        params
      )
      console.log('[AssessmentDetails] Specific assessment student list response:', JSON.stringify(response.data, null, 2));
      dispatch({
        type: FETCH_SPECIFIC_ASSESSMENT_STUDENT_LIST_SUCCESS,
        payload: response.data.data,
      })
    } catch (error) {
      console.error('Error fetching specific assessment student list:', error)
      dispatch({
        type: FETCH_SPECIFIC_ASSESSMENT_STUDENT_LIST_FAILURE,
        payload: error,
      })
    }
  }

// Diagnosis data actions
export const fetchDiagnosisData = (params) => async dispatch => {
  console.log('[fetchDiagnosisData] START - params:', params)

  try {
    dispatch({ type: FETCH_DIAGNOSIS_DATA_REQUEST })
    console.log('[fetchDiagnosisData] REQUEST dispatched')

    const response = await studentRequest.post(
      '/assessment/getDiagnosisDataForASchedule',
      {
        scheduleId: params.scheduleId,
        passingYear: params.passingYear || '',
        searchTerm: params.searchTerm || '',
        domain: params.domain || [],
        degree: params.degree || [],
        department: params.department || [],
        specialization: params.specialization || [],
        pageNumber: params.pageNumber || 1,
        pageSize: params.pageSize || 10,
        sortBy: params.sortBy || { field: 'name', order: 'asc' },
        // New filters
        Cefr: Array.isArray(params.cefrLevel) ? params.cefrLevel : (params.cefrLevel ? [params.cefrLevel] : []),
        AptitudeLevel: Array.isArray(params.aptitudeLevel) ? params.aptitudeLevel : (params.aptitudeLevel ? [params.aptitudeLevel] : []),
        consistency: Array.isArray(params.consistency) ? params.consistency : (params.consistency ? [params.consistency] : [])
      }
    )

    console.log('[fetchDiagnosisData] API response:', response)
    console.log('[fetchDiagnosisData] response.data:', response.data)
    console.log('[fetchDiagnosisData] response.data.data:', response.data?.data)

    const message = response.data?.message?.toLowerCase()
    console.log('[fetchDiagnosisData] message:', message)

    if (message === 'success') {
      const payload = response.data.data
      console.log('[fetchDiagnosisData] SUCCESS - payload to dispatch:', payload)

      dispatch({
        type: FETCH_DIAGNOSIS_DATA_SUCCESS,
        payload: payload
      })

      console.log('[fetchDiagnosisData] SUCCESS action dispatched')
      return payload
    } else {
      throw new Error(response.data?.message || 'Failed to fetch diagnosis data')
    }
  } catch (error) {
    console.error('[fetchDiagnosisData] ERROR:', error)
    dispatch({
      type: FETCH_DIAGNOSIS_DATA_FAILURE,
      payload: error.message
    })
    throw error
  }
}

/**
 * Export student list to Excel
 * Calls the export API and triggers browser download
 */
export const exportStudentListToExcel = (params = {}) => async dispatch => {
  try {
    console.log('[exportStudentListToExcel] Starting export with params:', params)

    const response = await studentRequest.post(
      `/assessment/exportStudentListForAssessment/${params.instituteId}`,
      {
        assessmentId: params.assessmentId,
        assessmentType: params.assessmentType || '',
        search: params.searchTerm || '',
        passingYear: params.passingYear || '',
        domain: params.domain || [],
        degree: params.degree || [],
        department: params.department || [],
        specialization: params.specialization || [],
        Cefr: params.Cefr || [],
        AptitudeLevel: params.AptitudeLevel || [],
        consistency: params.consistency || [],
        statusFilter: params.statusFilter || [],
        sortBy: params.sortBy || { field: 'name', order: 'asc' }
      },
      {
        responseType: 'blob'
      }
    )

    // Extract filename from Content-Disposition header
    let filename = 'Assessment Report.xlsx'
    const contentDisposition = response.headers['content-disposition']
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1]
      }
    }

    // Create download link
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    console.log('[exportStudentListToExcel] Download triggered successfully')
    return true
  } catch (error) {
    console.error('[exportStudentListToExcel] ERROR:', error)
    throw error
  }
}

/**
 * Export diagnosis data to Excel
 * Calls the export API and triggers browser download
 */
export const exportDiagnosisDataToExcel = (params = {}) => async dispatch => {
  try {
    console.log('[exportDiagnosisDataToExcel] Starting export with params:', params)

    const response = await studentRequest.post(
      `/assessment/exportDiagnosisDataForSchedule`,
      {
        scheduleId: params.scheduleId,
        searchTerm: params.searchTerm || '',
        passingYear: params.passingYear || '',
        domain: params.domain || [],
        degree: params.degree || [],
        department: params.department || [],
        specialization: params.specialization || [],
        Cefr: params.Cefr || [],
        AptitudeLevel: params.AptitudeLevel || [],
        consistency: params.consistency || [],
        sortBy: params.sortBy || { field: 'name', order: 'asc' }
      },
      {
        responseType: 'blob'
      }
    )

    // Extract filename from Content-Disposition header
    let filename = 'Diagnosis Report.xlsx'
    const contentDisposition = response.headers['content-disposition']
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1]
      }
    }

    // Create download link
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    console.log('[exportDiagnosisDataToExcel] Download triggered successfully')
    return true
  } catch (error) {
    console.error('[exportDiagnosisDataToExcel] ERROR:', error)
    throw error
  }
}

/**
 * Export TPO Dashboard student list to Excel
 * Calls the export API and triggers browser download
 */
export const fetchProgressionTimeline = (email, type, instituteId, upToAssessmentId) => async () => {
  try {
    const params = { email, type }
    if (instituteId) params.instituteId = instituteId
    if (upToAssessmentId) params.upToAssessmentId = upToAssessmentId
    const response = await studentRequest.get('/assessment/progression-timeline', { params })
    return response?.data?.data || { timeline: [], delta: null }
  } catch (error) {
    console.error('[fetchProgressionTimeline] ERROR:', error)
    return { timeline: [], delta: null }
  }
}

export const exportScheduleToExcel = (scheduleId, instituteId, passingYear) => async () => {
  try {
    const response = await studentRequest.post(
      `/assessment/exportSchedule/${scheduleId}`,
      { instituteId, passingYear },
      { responseType: 'blob' }
    )
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `schedule-${scheduleId}.xlsx`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('[exportScheduleToExcel] ERROR:', error)
    throw error
  }
}

export const exportTpoStudentListToExcel = (params = {}) => async dispatch => {
  try {
    console.log('[exportTpoStudentListToExcel] Starting export with params:', params)

    const response = await studentRequest.post(
      `/assessment/exportTpoStudentList/${params.instituteId}`,
      {
        searchParams: params.searchParams || '',
        passingYear: params.passingYear || '',
        domain: params.domain || [],
        degree: params.degree || [],
        department: params.department || [],
        specialization: params.specialization || [],
        sortBy: params.sortBy || { field: 'name', order: 'asc' }
      },
      {
        responseType: 'blob'
      }
    )

    // Extract filename from Content-Disposition header
    let filename = 'TPO Student List.xlsx'
    const contentDisposition = response.headers['content-disposition']
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1]
      }
    }

    // Create download link
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    console.log('[exportTpoStudentListToExcel] Download triggered successfully')
    return true
  } catch (error) {
    console.error('[exportTpoStudentListToExcel] ERROR:', error)
    throw error
  }
}
